import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import AdminShell from './AdminShell'
import { portalApi, tournamentApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'

/**
 * Try the PlayStay-API path first (proxied on every environment), fall back
 * to the tournament-api path (works wherever /api/* is proxied — e.g. the
 * local Vite dev server). This way the page works regardless of which of the
 * two backends has the latest code at any given moment, including during a
 * phased deploy where the SPA's bundle lands before the backend's route does.
 */
async function migGet(path, config) {
  try { return await portalApi.get(path, config) }
  catch (e) {
    const s = e?.response?.status
    if (s !== 404 && s !== 502) throw e
    return await tournamentApi.get(path, config)
  }
}
async function migPost(path, body, config) {
  try { return await portalApi.post(path, body, config) }
  catch (e) {
    const s = e?.response?.status
    if (s !== 404 && s !== 502) throw e
    return await tournamentApi.post(path, body, config)
  }
}

/**
 * /admin/data-migration — super-admin Data Migration page.
 *
 * Lets the super admin download a snapshot of the reseller-platform stack
 * (accounts/users/courses/integrations/groupings/packages/markups/api keys)
 * from this environment, then upload that file on another environment to
 * populate it. No SSH or scripts — the API runs both sides.
 */
// Tabs organize the growing list of admin tools into four buckets so
// nothing gets lost. Active tab persists across reloads via localStorage.
const TABS = [
  { id: 'schema',   label: 'Schema',         desc: 'CREATE / ALTER state of the DB itself.' },
  { id: 'data',     label: 'Course Data',    desc: 'One-off cleanups and seeds against course/integration rows.' },
  { id: 'sync',     label: 'Cross-env Sync', desc: 'Mirror IsApiOnly courses between localhost and prod.' },
  { id: 'transfer', label: 'Export / Import', desc: 'Download/upload snapshot files between environments.' },
  { id: 'testing',  label: 'Testing',        desc: 'Payment gateway sanity checks. Safe to run — no money moves.' },
  { id: 'diagnostics', label: 'Diagnostics', desc: 'Read-only probes against live provider APIs. Run these on production — some provider APIs allowlist the prod server’s IP.' },
]

export default function DataMigration() {
  const location = useLocation()
  const [tab, setTab] = useState(() => {
    const q = new URLSearchParams(window.location.search).get('tab')
    if (q && TABS.some(t => t.id === q)) return q
    return localStorage.getItem('admin_dm_tab') || 'schema'
  })
  function pickTab(id) { setTab(id); localStorage.setItem('admin_dm_tab', id) }

  // Deep-link: /admin/data-migration?tab=testing (e.g. the Integrations nav
  // item) opens that tab even when already on the page.
  useEffect(() => {
    const q = new URLSearchParams(location.search).get('tab')
    if (q && TABS.some(t => t.id === q)) pickTab(q)
  }, [location.search])

  const active = TABS.find(t => t.id === tab) || TABS[0]

  return (
    <AdminShell title="Data Migration">
      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e2e8f0', marginBottom: 16, maxWidth: 1100 }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => pickTab(t.id)}
            style={{
              padding: '10px 18px',
              background: tab === t.id ? '#fff' : 'transparent',
              border: 'none',
              borderBottom: tab === t.id ? '2px solid #0f172a' : '2px solid transparent',
              color: tab === t.id ? '#0f172a' : '#64748b',
              fontWeight: tab === t.id ? 600 : 500,
              fontSize: 14,
              cursor: 'pointer',
              marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      <p style={{ marginTop: 0, marginBottom: 24, color: '#475569', maxWidth: 760, fontSize: 13 }}>
        {active.desc}
      </p>

      {tab === 'schema' && (
        <>
          <div style={{ marginBottom: 24, maxWidth: 1100 }}><SchemaSyncCard /></div>
          <div style={{ marginBottom: 24, maxWidth: 1100 }}><SchemaAuditCard /></div>
          <div style={{ marginBottom: 24, maxWidth: 1100 }}><SeedProviderClassesCard /></div>
        </>
      )}

      {tab === 'data' && (
        <>
          <div style={{ marginBottom: 24, maxWidth: 1100 }}><MarkApiOnlyCard /></div>
          <div style={{ marginBottom: 24, maxWidth: 1100 }}><BackfillIpFromCipCard /></div>
          <div style={{ marginBottom: 24, maxWidth: 1100 }}><BackfillPackageCoursesCard /></div>
          <div style={{ marginBottom: 24, maxWidth: 1100 }}><DedupeIntegrationProvidersCard /></div>
          <div style={{ marginBottom: 24, maxWidth: 1100 }}><IntegolfCoursesImportCard /></div>
        </>
      )}

      {tab === 'sync' && (
        <>
          <div style={{ marginBottom: 24, maxWidth: 1100 }}><ApiOnlyCoursesSyncCard /></div>
          <div style={{ marginBottom: 24, maxWidth: 1100 }}><PushApiOnlyToTargetCard /></div>
        </>
      )}

      {tab === 'transfer' && (
        <>
          <p style={{ marginTop: 0, marginBottom: 16, color: '#475569', maxWidth: 760, fontSize: 13 }}>
            Use Download to snapshot this env, then sign into the other env's admin and use Upload to restore. Imports preserve IDs and skip rows that already exist — re-running is safe.
          </p>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', maxWidth: 1100 }}>
            <ExportCard />
            <ImportCard />
          </div>
        </>
      )}

      {tab === 'testing' && (
        <>
          <div style={{ marginBottom: 24, maxWidth: 1100 }}><ReconciliationCard /></div>
          <div style={{ marginBottom: 24, maxWidth: 1100 }}><MonerisPaymentTestCard /></div>
          <div style={{ marginBottom: 24, maxWidth: 1100 }}><BookingToolsCard /></div>
          <div style={{ marginBottom: 24, maxWidth: 1100 }}><ExpediaConfigCard /></div>
          <div style={{ marginBottom: 24, maxWidth: 1100 }}><ExpediaCardsSection /></div>
          <div style={{ maxWidth: 1100 }}><RateHawkCard /></div>
        </>
      )}

      {tab === 'diagnostics' && (
        <>
          <div style={{ marginBottom: 24, maxWidth: 1100 }}><EmailTestCard /></div>
          <div style={{ marginBottom: 24, maxWidth: 1100 }}><CourseDuplicatesCard /></div>
          <div style={{ marginBottom: 24, maxWidth: 1100 }}><TtcRateToolCard /></div>
          <div style={{ marginBottom: 24, maxWidth: 1100 }}><TeeTimeCourseProbeCard /></div>
          <div style={{ marginBottom: 24, maxWidth: 1100 }}><TtcCourseDiagnosticsCard /></div>
          <div style={{ marginBottom: 24, maxWidth: 1100 }}><MarkupDebugCard /></div>
          <div style={{ marginBottom: 24, maxWidth: 1100 }}><UnderpaymentAuditCard /></div>
          <div style={{ marginBottom: 24, maxWidth: 1100 }}><NegativeMarginAuditCard /></div>
        </>
      )}
    </AdminShell>
  )
}

// ─── Moneris payment test (pre-auth + $0.00 reversal, no money moved) ─────

/**
 * MonerisPaymentTestCard — one-click verification that the active Moneris
 * store is wired for the reseller embed checkout flow (pre-auth then
 * completion). It runs a small pre-authorization on the card you enter, then
 * immediately reverses it with a $0.00 completion so the hold is released and
 * nothing is ever captured. Net charge is $0.00.
 *
 * The card is sent over HTTPS to the API for the single test call and is never
 * stored (only the last 4 come back for the result panel). This is the safe
 * way to confirm a LIVE merchant account before real customers check out.
 */
function MonerisPaymentTestCard() {
  const [amount, setAmount] = useState('0.01')
  const [currency, setCurrency] = useState('USD')
  const [card, setCard] = useState({ number: '', expiry: '', cvc: '' })
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')
  const [mcpBusy, setMcpBusy] = useState(false)
  const [mcp, setMcp] = useState(null)

  // MCP rate check — verifies the store can price USD (settle CAD). No card, no charge.
  async function checkMcp() {
    setMcpBusy(true); setMcp(null); setError('')
    try {
      const { data } = await tournamentApi.post('/admin/data-migration/moneris-mcp-rate', { amount: Number(amount) > 0 ? Number(amount) : 1 })
      setMcp(data)
    } catch (err) {
      setMcp({ ok: false, verdict: err?.response?.data?.message || err?.message || 'MCP rate check failed' })
    } finally { setMcpBusy(false) }
  }

  function setNum(v) {
    const digits = v.replace(/\D/g, '').slice(0, 19)
    setCard(c => ({ ...c, number: digits.replace(/(.{4})/g, '$1 ').trim() }))
  }
  function setExp(v) {
    const digits = v.replace(/\D/g, '').slice(0, 4)
    setCard(c => ({ ...c, expiry: digits.length >= 3 ? digits.slice(0, 2) + ' / ' + digits.slice(2) : digits }))
  }
  function setCvc(v) { setCard(c => ({ ...c, cvc: v.replace(/\D/g, '').slice(0, 4) })) }

  const ready = card.number.replace(/\s/g, '').length >= 13
    && card.expiry.replace(/\D/g, '').length === 4
    && card.cvc.length >= 3

  async function run() {
    setBusy(true); setError(''); setResult(null)
    try {
      const { data } = await tournamentApi.post('/admin/data-migration/moneris-test', {
        amount: Number(amount) > 0 ? Number(amount) : 0.01,
        currency,
        card: { number: card.number, expiry: card.expiry, cvc: card.cvc },
      })
      setResult(data)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Payment test failed')
    } finally { setBusy(false) }
  }

  const isLive = result?.mode === 'live'

  return (
    <Card
      title="Moneris payment test"
      subtitle="Confirms the active Moneris store supports the embed checkout (pre-auth then completion). Runs a pre-authorization, then reverses it with a $0.00 completion — nothing is captured, so no money moves. Use a real card (yours or a company card) to verify the LIVE account."
    >
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 4 }}>
        <label style={{ fontSize: 13, color: '#334155', display: 'flex', flexDirection: 'column', gap: 4 }}>
          Hold amount (reversed immediately)
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ color: '#64748b' }}>$</span>
            <input value={amount} onChange={e => setAmount(e.target.value.replace(/[^\d.]/g, ''))} inputMode="decimal" style={{ ...testInp, width: 90 }} />
          </div>
        </label>
        <label style={{ fontSize: 13, color: '#334155', display: 'flex', flexDirection: 'column', gap: 4 }}>
          Currency to test
          <select value={currency} onChange={e => setCurrency(e.target.value)} style={{ ...testInp, width: 90 }}>
            <option value="USD">USD</option>
            <option value="CAD">CAD</option>
          </select>
        </label>
        <label style={{ fontSize: 13, color: '#334155', display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 200px' }}>
          Card number
          <input value={card.number} onChange={e => setNum(e.target.value)} inputMode="numeric" autoComplete="off" placeholder="1234 5678 9012 3456" style={{ ...testInp, fontFamily: 'monospace', letterSpacing: 1 }} />
        </label>
        <label style={{ fontSize: 13, color: '#334155', display: 'flex', flexDirection: 'column', gap: 4 }}>
          Expiry (MM/YY)
          <input value={card.expiry} onChange={e => setExp(e.target.value)} inputMode="numeric" autoComplete="off" placeholder="08 / 28" style={{ ...testInp, width: 90, fontFamily: 'monospace' }} />
        </label>
        <label style={{ fontSize: 13, color: '#334155', display: 'flex', flexDirection: 'column', gap: 4 }}>
          CVC
          <input value={card.cvc} onChange={e => setCvc(e.target.value)} inputMode="numeric" autoComplete="off" placeholder="123" style={{ ...testInp, width: 70, fontFamily: 'monospace' }} />
        </label>
      </div>

      <button onClick={run} disabled={busy || !ready} style={{ ...primaryBtn, opacity: (busy || !ready) ? 0.55 : 1, cursor: (busy || !ready) ? 'not-allowed' : 'pointer' }}>
        {busy
          ? (currency === 'USD' ? 'Running MCP pre-auth + reversal…' : 'Running pre-auth + reversal…')
          : (currency === 'USD' ? 'Run MCP pre-auth + reversal' : 'Run pre-auth + reversal')}
      </button>
      <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 8, marginBottom: 8 }}>
        The card is used only for this single test and is never stored.
      </p>

      <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
        <button onClick={checkMcp} disabled={mcpBusy} style={{ ...primaryBtn, background: '#0369a1', opacity: mcpBusy ? 0.55 : 1, cursor: mcpBusy ? 'not-allowed' : 'pointer' }}>
          {mcpBusy ? 'Checking USD rate…' : 'Check USD rate (MCP) — no card, no charge'}
        </button>
        <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 8, marginBottom: 0 }}>
          Confirms the store can price in USD and settle CAD (Multi-Currency Pricing). Just a rate lookup — no card, no transaction.
        </p>
        {mcp && (
          <div style={{ marginTop: 10, padding: 12, borderRadius: 8, fontSize: 13,
            background: mcp.ok ? '#f0fdf4' : '#fef2f2', border: `1px solid ${mcp.ok ? '#86efac' : '#fca5a5'}` }}>
            <div style={{ fontWeight: 600, color: mcp.ok ? '#15803d' : '#b91c1c' }}>{mcp.ok ? '✓ ' : '✗ '}{mcp.verdict}</div>
            {(mcp.mcpRate || mcp.settlementAmount) && (
              <div style={{ fontSize: 12, color: '#475569', marginTop: 6 }}>
                rate <code>{mcp.mcpRate || '—'}</code> · ${mcp.usdAmount || '—'} USD → ${mcp.settlementAmount || '—'} CAD · store <code>{mcp.storeId}</code> ({mcp.mode})
              </div>
            )}
          </div>
        )}
      </div>

      {error && <p style={errorStyle}>{error}</p>}

      {result && (
        <div style={{
          marginTop: 16, padding: 14, borderRadius: 8,
          background: result.ok ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${result.ok ? '#86efac' : '#fca5a5'}`,
        }}>
          <div style={{ fontWeight: 600, marginBottom: 8, color: result.ok ? '#15803d' : '#b91c1c' }}>
            {result.ok ? '✓ ' : '✗ '}{result.verdict}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            <span style={{
              fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
              background: isLive ? '#fee2e2' : '#e0f2fe', color: isLive ? '#b91c1c' : '#0369a1',
            }}>
              {isLive ? 'LIVE STORE' : 'SANDBOX'}
            </span>
            <span style={{ fontSize: 12, color: '#475569' }}>store <code>{result.storeId}</code> · {result.host}</span>
            {result.currencyTested && <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#f1f5f9', color: '#0f172a' }}>tested {result.currencyTested}</span>}
            {result.last4 && <span style={{ fontSize: 12, color: '#475569' }}>card •••• {result.last4}</span>}
          </div>
          {result.portalNote && (
            <div style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 10px', marginBottom: 10 }}>
              {result.portalNote}
            </div>
          )}
          <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', maxWidth: 560 }}>
            <thead>
              <tr style={{ color: '#64748b', textAlign: 'left' }}>
                <th style={testTh}>Step</th><th style={testTh}>Code</th><th style={testTh}>Amount</th><th style={testTh}>Txn ID</th><th style={testTh}>Message</th>
              </tr>
            </thead>
            <tbody>
              <TestRow label="Pre-auth (hold)" r={result.preauth} />
              <TestRow label="Reversal ($0.00)" r={result.reversal} />
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

function TestRow({ label, r }) {
  if (!r) return (
    <tr><td style={testTd}>{label}</td><td style={testTd} colSpan={4}><span style={{ color: '#94a3b8' }}>not run</span></td></tr>
  )
  return (
    <tr>
      <td style={testTd}>{label}</td>
      <td style={testTd}><code>{r.responseCode ?? '—'}</code></td>
      <td style={testTd}>{r.transAmount != null ? `$${r.transAmount}` : '—'}</td>
      <td style={testTd}><code style={{ fontSize: 11 }}>{r.transactionId || '—'}</code></td>
      <td style={testTd}>{r.message || '—'}</td>
    </tr>
  )
}

const testInp = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, outline: 'none' }
const testTh = { padding: '4px 8px', borderBottom: '1px solid #e2e8f0', fontWeight: 600 }
const testTd = { padding: '4px 8px', borderBottom: '1px solid #f1f5f9', color: '#334155', verticalAlign: 'top' }

// ─── Expedia hotel-booking readiness ─────────────────────────────────────

/**
 * ExpediaConfigCard — one-click check of whether the active Expedia provider
 * can book real hotel inventory: reports the mode (dev = test card only, live
 * = real bookings) and, in live mode, which corporate-card fields are set.
 * Reads booleans only — never the card values.
 */
function ExpediaConfigCard() {
  const [busy, setBusy] = useState(false)
  const [switching, setSwitching] = useState(false)
  const [data, setData] = useState(null)
  const [error, setError] = useState('')

  async function run() {
    setBusy(true); setError(''); setData(null)
    try {
      const { data } = await tournamentApi.post('/admin/data-migration/expedia-config-check', {})
      setData(data)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Check failed')
    } finally { setBusy(false) }
  }

  async function setMode(mode) {
    const msg = mode === 'live'
      ? 'Switch Expedia to LIVE? New bookings will reserve REAL hotel inventory and charge the InteGolf corporate card. The hotel-search cache will be cleared.'
      : 'Switch Expedia back to dev (sandbox / test card)?'
    if (!confirm(msg)) return
    setSwitching(true); setError('')
    try {
      await tournamentApi.post('/admin/data-migration/expedia-set-mode', { mode })
      await run()
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Switch failed')
    } finally { setSwitching(false) }
  }

  const good = data?.liveReady && data?.cardDecryptable !== false
  return (
    <Card
      title="Expedia hotel-booking readiness"
      subtitle="Checks whether the active Expedia provider can book real hotels — mode (dev = test card only, live = real bookings) and, in live mode, which corporate-card fields are set. Reads booleans only; card values are never returned."
    >
      <button onClick={run} disabled={busy} style={{ ...primaryBtn, marginTop: 0, opacity: busy ? 0.55 : 1 }}>
        {busy ? 'Checking…' : 'Check Expedia config'}
      </button>
      {error && <p style={errorStyle}>{error}</p>}
      {data && (
        <div style={{ marginTop: 14, padding: 14, borderRadius: 8, background: good ? '#f0fdf4' : '#fef2f2', border: `1px solid ${good ? '#86efac' : '#fca5a5'}` }}>
          <div style={{ fontWeight: 600, marginBottom: 8, color: good ? '#15803d' : '#b91c1c' }}>
            {good ? '✓ ' : '✗ '}{data.verdict || data.message}
          </div>
          {data.found && (
            <>
              <div style={{ fontSize: 12, color: '#475569', marginBottom: 10 }}>
                provider #{data.providerId} · <code>{data.providerType}</code> · mode{' '}
                <span style={{ fontWeight: 700, color: data.mode === 'live' ? '#b45309' : '#0369a1' }}>{data.mode}</span>
                {data.liveCapable != null && (
                  <span style={{ marginLeft: 8 }}>· live-capable:{' '}
                    <span style={{ fontWeight: 700, color: data.liveCapable ? '#15803d' : '#b91c1c' }}>{data.liveCapable ? 'yes' : 'no'}</span>
                  </span>
                )}
                {data.cardDecryptable != null && (
                  <span style={{ marginLeft: 8 }}>· card decrypts:{' '}
                    <span style={{ fontWeight: 700, color: data.cardDecryptable ? '#15803d' : '#b91c1c' }}>{data.cardDecryptable ? 'yes' : 'no'}</span>
                  </span>
                )}
                <span style={{ marginLeft: 8 }}>· backup card:{' '}
                  {!data.backupCard?.present
                    ? <span style={{ fontWeight: 700, color: '#94a3b8' }}>none</span>
                    : <span style={{ fontWeight: 700, color: data.backupCard.decryptable ? '#15803d' : '#b91c1c' }}>
                        {data.backupCard.decryptable ? `ready (•••• ${data.backupCard.last4})` : "set but won't decrypt"}
                      </span>}
                </span>
              </div>
              <table style={{ borderCollapse: 'collapse', fontSize: 12, maxWidth: 420 }}>
                <tbody>
                  {data.apiCreds && Object.entries(data.apiCreds).map(([k, v]) => (
                    <tr key={k}>
                      <td style={testTd}><code style={{ fontSize: 11 }}>{k}</code></td>
                      <td style={{ ...testTd, color: v ? '#15803d' : '#b91c1c', fontWeight: 600 }}>{v ? 'set' : 'missing'}</td>
                    </tr>
                  ))}
                  {data.cardFields && Object.entries(data.cardFields).map(([k, v]) => (
                    <tr key={k}>
                      <td style={testTd}><code style={{ fontSize: 11 }}>{k}</code></td>
                      <td style={{ ...testTd, color: v ? '#15803d' : '#b91c1c', fontWeight: 600 }}>{v ? 'set' : 'missing'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {data.mode !== 'live' && data.liveCapable && data.cardDecryptable && (
                  <button onClick={() => setMode('live')} disabled={switching}
                    style={{ padding: '10px 18px', fontSize: 14, fontWeight: 600, background: '#b45309', color: '#fff', border: 'none', borderRadius: 6, cursor: switching ? 'not-allowed' : 'pointer', opacity: switching ? 0.55 : 1 }}>
                    {switching ? 'Switching…' : 'Switch to LIVE (real bookings)'}
                  </button>
                )}
                {data.mode !== 'live' && data.liveCapable && data.cardDecryptable === false && (
                  <span style={{ fontSize: 12, color: '#b91c1c', alignSelf: 'center' }}>Card can't be decrypted here — use "Set Expedia live card" below before switching to live.</span>
                )}
                {data.mode === 'live' && (
                  <button onClick={() => setMode('dev')} disabled={switching}
                    style={{ padding: '10px 18px', fontSize: 14, fontWeight: 600, background: '#0369a1', color: '#fff', border: 'none', borderRadius: 6, cursor: switching ? 'not-allowed' : 'pointer', opacity: switching ? 0.55 : 1 }}>
                    {switching ? 'Switching…' : 'Switch back to dev (sandbox)'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  )
}

// ─── Expedia corporate cards (primary + backup + swap) ──────────────────

/**
 * ExpediaCardsSection — the primary + backup card setters with each card's
 * current last-4 shown, plus a "Swap cards" button that makes the backup the
 * main card and vice-versa (backend swaps the encrypted values in place).
 */
function ExpediaCardsSection() {
  const [cards, setCards] = useState({ primary: null, backup: null })
  const [swapping, setSwapping] = useState(false)
  const [swapMsg, setSwapMsg] = useState('')

  async function refresh() {
    try {
      const { data } = await tournamentApi.post('/admin/data-migration/expedia-config-check', {})
      setCards({ primary: data?.primaryCard?.last4 || null, backup: data?.backupCard?.last4 || null })
    } catch { /* non-fatal */ }
  }
  useEffect(() => { refresh() }, [])

  async function swap() {
    if (!confirm('Swap the primary and backup Expedia cards? The current backup becomes the main card and vice-versa.')) return
    setSwapping(true); setSwapMsg('')
    try {
      const { data } = await tournamentApi.post('/admin/data-migration/expedia-swap-cards', {})
      setSwapMsg(data.message || 'Swapped.')
      await refresh()
    } catch (err) {
      setSwapMsg(err?.response?.data?.message || err?.message || 'Swap failed')
    } finally { setSwapping(false) }
  }

  const canSwap = !!cards.primary && !!cards.backup

  return (
    <>
      <div style={{ marginBottom: 24 }}><ExpediaSetCardCard slot="primary" currentLast4={cards.primary} onSaved={refresh} /></div>
      <div style={{ marginBottom: 24 }}>
        <Card title="Swap primary ↔ backup" subtitle="Make the backup card the main card and the main card the backup. Only the card details move; the billing address stays put.">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, color: '#334155' }}>
              Primary <code style={{ fontWeight: 700 }}>•••• {cards.primary || '——'}</code>
              <span style={{ margin: '0 8px', color: '#94a3b8' }}>⇄</span>
              Backup <code style={{ fontWeight: 700 }}>•••• {cards.backup || '——'}</code>
            </div>
            <button onClick={swap} disabled={swapping || !canSwap}
              style={{ ...primaryBtn, marginTop: 0, opacity: (swapping || !canSwap) ? 0.55 : 1, cursor: (swapping || !canSwap) ? 'not-allowed' : 'pointer' }}>
              {swapping ? 'Swapping…' : 'Swap cards'}
            </button>
          </div>
          {!canSwap && <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 10 }}>Both a primary and a backup card must be set to swap.</p>}
          {swapMsg && <p style={{ fontSize: 13, color: '#15803d', fontWeight: 600, marginTop: 10 }}>{swapMsg}</p>}
        </Card>
      </div>
      <div style={{ marginBottom: 24 }}><ExpediaSetCardCard slot="backup" currentLast4={cards.backup} onSaved={refresh} /></div>
    </>
  )
}

// ─── Set Expedia live corporate card ─────────────────────────────────────

/**
 * ExpediaSetCardCard — (re)enter the Expedia live corporate card so it's
 * encrypted with THIS server's key and decrypts correctly here. Use when the
 * readiness check reports "card decrypts: no" (row encrypted with a different
 * key). The card is sent over HTTPS, encrypted at rest, and never returned.
 */
function ExpediaSetCardCard({ slot = 'primary', currentLast4, onSaved }) {
  const isBackup = slot === 'backup'
  const [num, setNum] = useState('')
  const [exp, setExp] = useState('')
  const [cvc, setCvc] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')

  const digits = num.replace(/\D/g, '')
  const expDigits = exp.replace(/\D/g, '')
  const ready = digits.length >= 12 && cvc.replace(/\D/g, '').length >= 3

  async function save() {
    if (!confirm(`Save this card as the Expedia ${isBackup ? 'BACKUP' : 'live'} corporate card on this environment? It will be encrypted at rest.`)) return
    setBusy(true); setError(''); setResult(null)
    try {
      const body = { number: digits, cvc: cvc.replace(/\D/g, ''), slot }
      if (expDigits.length === 4) { body.expiryMonth = expDigits.slice(0, 2); body.expiryYear = '20' + expDigits.slice(2) }
      const { data } = await tournamentApi.post('/admin/data-migration/expedia-set-card', body)
      setResult(data)
      if (data.ok) { setNum(''); setExp(''); setCvc(''); onSaved?.() }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Save failed')
    } finally { setBusy(false) }
  }

  return (
    <Card
      title={isBackup ? 'Set Expedia BACKUP corporate card' : 'Set Expedia live corporate card'}
      subtitle={isBackup
        ? "The fallback card. If a hotel booking is declined on the main corporate card, the system automatically retries the booking on this one. Same corporate account is assumed (it reuses the main card's billing address). Encrypted at rest, never returned (only the last 4)."
        : "Re-enter the InteGolf Expedia corporate card so it's encrypted with this server's key and decrypts here. Needed when the readiness check shows 'card decrypts: no'. Sent over HTTPS, encrypted at rest, never returned (only the last 4)."}
    >
      <div style={{ fontSize: 13, color: '#334155', marginBottom: 14 }}>
        Current card on file:{' '}
        {currentLast4
          ? <code style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>•••• {currentLast4}</code>
          : <span style={{ color: '#94a3b8' }}>none set</span>}
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ fontSize: 13, color: '#334155', display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 240px' }}>
          Card number
          <input value={num} onChange={e => setNum(e.target.value.replace(/\D/g, '').slice(0, 19).replace(/(.{4})/g, '$1 ').trim())} inputMode="numeric" autoComplete="off" placeholder="4537 3530 5462 7010" style={{ ...testInp, fontFamily: 'monospace', letterSpacing: 1 }} />
        </label>
        <label style={{ fontSize: 13, color: '#334155', display: 'flex', flexDirection: 'column', gap: 4 }}>
          Expiry (MM/YY)
          <input value={exp} onChange={e => { const d = e.target.value.replace(/\D/g, '').slice(0, 4); setExp(d.length >= 3 ? d.slice(0, 2) + ' / ' + d.slice(2) : d) }} inputMode="numeric" autoComplete="off" placeholder="08 / 28" style={{ ...testInp, width: 90, fontFamily: 'monospace' }} />
        </label>
        <label style={{ fontSize: 13, color: '#334155', display: 'flex', flexDirection: 'column', gap: 4 }}>
          CVC
          <input value={cvc} onChange={e => setCvc(e.target.value.replace(/\D/g, '').slice(0, 4))} inputMode="numeric" autoComplete="off" placeholder="123" style={{ ...testInp, width: 70, fontFamily: 'monospace' }} />
        </label>
      </div>
      <button onClick={save} disabled={busy || !ready} style={{ ...primaryBtn, opacity: (busy || !ready) ? 0.55 : 1, cursor: (busy || !ready) ? 'not-allowed' : 'pointer' }}>
        {busy ? 'Saving…' : (isBackup ? 'Save backup card' : 'Save Expedia card')}
      </button>
      {error && <p style={errorStyle}>{error}</p>}
      {result && (
        <p style={{ marginTop: 12, padding: 12, borderRadius: 8, fontSize: 13, fontWeight: 600,
          background: result.ok ? '#f0fdf4' : '#fef2f2', border: `1px solid ${result.ok ? '#86efac' : '#fca5a5'}`, color: result.ok ? '#15803d' : '#b91c1c' }}>
          {result.ok ? '✓ ' : '✗ '}{result.message}{result.last4 ? ` (•••• ${result.last4})` : ''}
        </p>
      )}
    </Card>
  )
}

// ─── RateHawk (ETG) backup hotel provider ────────────────────────────────

/**
 * RateHawkCard — save the RateHawk (ETG) API credentials into
 * integration_providers (so no separate integrations page is needed), and run a
 * read-only connectivity + coverage probe. The probe runs server-side, so on
 * prod it executes from the prod egress IP the RateHawk prod key must be
 * allowlisted for — and reports that IP on a 'not_allowed_host' rejection.
 */
function RateHawkCard() {
  const [username, setUsername] = useState('')
  const [apiKey, setApiKey]     = useState('')
  const [baseUrl, setBaseUrl]   = useState('https://api.worldota.net/api/b2b/v3')
  const [mode, setMode]         = useState('live')
  const [active, setActive]     = useState(true)
  const [searchEnabled, setSearchEnabled] = useState(false)
  const [busy, setBusy]         = useState('')
  const [saveRes, setSaveRes]   = useState(null)
  const [probe, setProbe]       = useState(null)
  const [error, setError]       = useState('')

  async function save() {
    const hasKey = username.trim() && apiKey.trim()
    if ((username.trim() || apiKey.trim()) && !hasKey) { setError('Enter BOTH username and API key, or leave both blank to change only the settings.'); return }
    if (searchEnabled && !confirm('Turn RateHawk ON in customer search? Customers will start seeing — and booking — RateHawk hotels. Only do this after a successful test booking.')) return
    if (!confirm(`Save RateHawk settings on THIS environment (${active ? 'active' : 'inactive'}, customer search ${searchEnabled ? 'ON' : 'off'})${hasKey ? ' — with new credentials' : ''}?`)) return
    setBusy('save'); setError(''); setSaveRes(null)
    try {
      const body = { active, searchEnabled }
      if (hasKey) { body.username = username.trim(); body.apiKey = apiKey.trim(); body.baseUrl = baseUrl.trim(); body.mode = mode }
      const { data } = await tournamentApi.post('/admin/data-migration/ratehawk-set-config', body)
      setSaveRes(data)
      if (data.ok) setApiKey('')
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Save failed')
    } finally { setBusy('') }
  }

  async function test() {
    setBusy('test'); setError(''); setProbe(null)
    try {
      const { data } = await tournamentApi.post('/admin/data-migration/ratehawk-probe', {})
      setProbe(data)
      if (typeof data.searchEnabled === 'boolean') setSearchEnabled(data.searchEnabled) // reflect live state
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Probe failed')
    } finally { setBusy('') }
  }

  const outlineBtn = { padding: '10px 18px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#0f172a', fontWeight: 600, fontSize: 13, cursor: 'pointer' }

  return (
    <Card
      title="RateHawk (ETG) hotel provider"
      subtitle="Save the RateHawk API credentials here (no integrations page needed), then run a read-only connectivity + coverage probe. The probe runs from THIS server — on production that's the prod egress IP the RateHawk prod key must be allowlisted for. No booking, no money."
    >
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ fontSize: 13, color: '#334155', display: 'flex', flexDirection: 'column', gap: 4 }}>
          Username (key id)
          <input value={username} onChange={e => setUsername(e.target.value)} autoComplete="off" placeholder="11297" style={{ ...testInp, width: 120 }} />
        </label>
        <label style={{ fontSize: 13, color: '#334155', display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 320px' }}>
          API key
          <input value={apiKey} onChange={e => setApiKey(e.target.value)} autoComplete="off" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" style={{ ...testInp, fontFamily: 'monospace' }} />
        </label>
        <label style={{ fontSize: 13, color: '#334155', display: 'flex', flexDirection: 'column', gap: 4 }}>
          Mode
          <select value={mode} onChange={e => setMode(e.target.value)} style={{ ...testInp, width: 100 }}>
            <option value="live">live</option>
            <option value="dev">dev</option>
          </select>
        </label>
        <label style={{ fontSize: 13, color: '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} /> Active
        </label>
        <label style={{ fontSize: 13, color: '#b45309', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
          <input type="checkbox" checked={searchEnabled} onChange={e => setSearchEnabled(e.target.checked)} /> Show in customer search (go-live)
        </label>
      </div>
      <label style={{ fontSize: 13, color: '#334155', display: 'flex', flexDirection: 'column', gap: 4, marginTop: 12, maxWidth: 460 }}>
        Base URL
        <input value={baseUrl} onChange={e => setBaseUrl(e.target.value)} autoComplete="off" style={{ ...testInp, fontFamily: 'monospace' }} />
      </label>
      <div style={{ display: 'flex', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
        <button onClick={save} disabled={!!busy} style={{ ...primaryBtn, marginTop: 0, opacity: busy ? 0.55 : 1, cursor: busy ? 'not-allowed' : 'pointer' }}>
          {busy === 'save' ? 'Saving…' : 'Save RateHawk settings'}
        </button>
        <button onClick={test} disabled={!!busy} style={{ ...outlineBtn, opacity: busy ? 0.55 : 1, cursor: busy ? 'not-allowed' : 'pointer' }}>
          {busy === 'test' ? 'Testing…' : 'Test RateHawk (connectivity + coverage)'}
        </button>
      </div>
      {error && <p style={errorStyle}>{error}</p>}
      {saveRes && (
        <p style={{ marginTop: 12, padding: 12, borderRadius: 8, fontSize: 13, fontWeight: 600,
          background: saveRes.ok ? '#f0fdf4' : '#fef2f2', border: `1px solid ${saveRes.ok ? '#86efac' : '#fca5a5'}`, color: saveRes.ok ? '#15803d' : '#b91c1c' }}>
          {saveRes.ok ? '✓ ' : '✗ '}{saveRes.message}
        </p>
      )}
      {probe && (
        <div style={{ marginTop: 14, padding: 14, borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0', fontSize: 13 }}>
          <div style={{ fontWeight: 700, marginBottom: 6, color: probe.ok ? '#15803d' : '#b91c1c' }}>
            {probe.ok ? '✓ RateHawk reachable' : '✗ ' + (probe.hint || 'RateHawk not reachable')}
          </div>
          <div style={{ color: '#475569', marginBottom: 6 }}>
            Connection: {probe.connection?.ok ? 'ok' : (probe.connection?.message || 'failed')} · mode: {probe.mode}
          </div>
          <div style={{ color: probe.searchEnabled ? '#15803d' : '#64748b', marginBottom: 6, fontWeight: 600 }}>
            Customer search: {probe.searchEnabled ? 'ON — live to customers' : 'off (not shown to customers yet)'}
          </div>
          {probe.egressIp && (
            <div style={{ marginBottom: 8, color: '#b45309' }}>
              Allowlist this egress IP with RateHawk: <code style={{ fontWeight: 700 }}>{probe.egressIp}</code>
            </div>
          )}
          {Array.isArray(probe.regions) && (
            <table style={{ borderCollapse: 'collapse', fontSize: 12.5 }}>
              <tbody>
                {probe.regions.map((r, i) => (
                  <tr key={i}>
                    <td style={{ padding: '2px 12px 2px 0', color: '#334155' }}>{r.region}</td>
                    <td style={{ padding: '2px 0' }}>
                      {r.error
                        ? <span style={{ color: '#b91c1c' }}>{r.error}</span>
                        : <span style={{ color: r.hotels > 0 ? '#15803d' : '#b45309', fontWeight: 600 }}>{r.hotels} hotels</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Card>
  )
}

// ─── Booking diagnostics + stuck-hold release ────────────────────────────

/**
 * ReconciliationCard — "what still has real money out?" on THIS environment.
 * Two tools:
 *   1. Outstanding report — every reseller payment still holding/charged (real,
 *      non-fake), flagged so money-out-on-a-dead-trip floats to the top.
 *   2. Itinerary checker — paste an Expedia itinerary number off a card
 *      statement + the guest email to see if that hotel room is still a live
 *      booking, and cancel it (Expedia refunds the card) if so.
 * Run it on production to reconcile the corporate-card statement.
 */
// Duplicate courses — one physical course exists as several records (different
// provider imports). Scan clusters by name; preview shows exactly what a merge
// would move + delete, and refuses to delete a record with operational data.
function CourseDuplicatesCard() {
  const [busy, setBusy] = useState('')
  const [scan, setScan] = useState(null)
  const [error, setError] = useState('')
  async function runScan() {
    setBusy('scan'); setError(''); setScan(null)
    try { const { data } = await tournamentApi.get('/admin/data-migration/course-duplicates'); setScan(data) }
    catch (err) { setError(err?.response?.data?.message || err?.message || 'Scan failed') }
    finally { setBusy('') }
  }
  return (
    <Card
      title="Duplicate courses"
      subtitle="Runs against THIS environment. Finds courses that exist as more than one record (different provider imports). Pick the record to keep, choose the duplicates, and Preview exactly what a merge would move and delete — no changes until you apply."
    >
      <button onClick={runScan} disabled={!!busy} style={{ ...primaryBtn, marginTop: 0, opacity: busy ? 0.55 : 1 }}>
        {busy === 'scan' ? 'Scanning…' : 'Scan for duplicates'}
      </button>
      {error && <p style={errorStyle}>{error}</p>}
      {scan && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 13, marginBottom: 10 }}>
            {scan.duplicateGroups === 0
              ? <span style={{ color: '#15803d', fontWeight: 600 }}>✓ No duplicate course names found ({scan.totalCourses} courses).</span>
              : <><strong>{scan.duplicateGroups}</strong> duplicate group(s) across {scan.totalCourses} courses.</>}
          </div>
          {scan.groups.map((g, i) => <DupGroup key={i} group={g} onMerged={runScan} />)}
        </div>
      )}
    </Card>
  )
}

function DupGroup({ group, onMerged }) {
  const best = [...group.records].sort((a, b) => b.packages - a.packages || b.stayplayRefs - a.stayplayRefs)[0]?.coursesId
  const [canonical, setCanonical] = useState(best)
  const [merge, setMerge] = useState({})
  const [preview, setPreview] = useState(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(null)
  function selectedMergeIds() { return group.records.map(r => r.coursesId).filter(id => id !== canonical && merge[id]) }
  async function doPreview() {
    const mergeIds = selectedMergeIds()
    if (!mergeIds.length) { alert('Check at least one record to merge into the one you are keeping.'); return }
    setBusy(true); setPreview(null); setDone(null)
    try { const { data } = await tournamentApi.post('/admin/data-migration/course-merge-preview', { canonicalId: canonical, mergeIds }); setPreview(data) }
    catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }
  async function doApply() {
    const mergeIds = selectedMergeIds()
    if (!mergeIds.length) return
    if (!confirm(`Permanently merge ${mergeIds.map(id => '#' + id).join(', ')} into #${canonical}? This repoints their packages/bookings and DELETES the duplicate records. Cannot be undone.`)) return
    setBusy(true)
    try {
      const { data } = await tournamentApi.post('/admin/data-migration/course-merge-apply', { canonicalId: canonical, mergeIds })
      setDone(data); setPreview(null)
      if (onMerged) setTimeout(onMerged, 600)
    } catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }
  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginBottom: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>{group.courseName} · {group.count} records</div>
      <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
        <thead><tr style={{ color: '#64748b', textAlign: 'left' }}>
          <th style={testTh}>Keep</th><th style={testTh}>Merge</th><th style={testTh}>#</th><th style={testTh}>Name / city</th><th style={testTh}>Provider</th><th style={testTh}>StayPlay footprint</th><th style={testTh}></th>
        </tr></thead>
        <tbody>
          {group.records.map(r => (
            <tr key={r.coursesId} style={{ background: r.coursesId === canonical ? '#f0fdf4' : 'transparent' }}>
              <td style={testTd}><input type="radio" checked={canonical === r.coursesId} onChange={() => { setCanonical(r.coursesId); setMerge(m => ({ ...m, [r.coursesId]: false })); setPreview(null) }} /></td>
              <td style={testTd}><input type="checkbox" disabled={canonical === r.coursesId || r.hasOperational} checked={!!merge[r.coursesId]} onChange={e => { setMerge(m => ({ ...m, [r.coursesId]: e.target.checked })); setPreview(null) }} /></td>
              <td style={testTd}>{r.coursesId}</td>
              <td style={testTd}>{r.courseName}<br /><span style={{ color: '#94a3b8' }}>{r.city || '—'}</span></td>
              <td style={testTd}>{r.providers.length ? r.providers.map(p => p.provider + (p.active ? '' : '·off')).join(', ') : <span style={{ color: '#cbd5e1' }}>none</span>}</td>
              <td style={testTd}>pkg {r.packages} · grp {r.groupings} · bk {r.bookings} · res {r.resellers}</td>
              <td style={testTd}>{r.hasOperational
                ? <span style={{ color: '#b91c1c', fontWeight: 700 }}>⚠ operational <span style={{ fontWeight: 400 }}>({Object.entries(r.operational || {}).map(([t, n]) => `${t} ${n}`).join(', ')})</span> — can't delete</span>
                : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <button onClick={doPreview} disabled={busy} style={{ ...primaryBtn, marginTop: 8 }}>{busy ? 'Previewing…' : 'Preview merge'}</button>
      {preview && (
        <div style={{ marginTop: 12, padding: 12, background: preview.canApply ? '#f8fafc' : '#fef2f2', border: '1px solid ' + (preview.canApply ? '#e2e8f0' : '#fecaca'), borderRadius: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
            Merge {preview.mergeIds.map(id => '#' + id).join(', ')} → keep <strong>#{preview.canonical.coursesId} {preview.canonical.courseName}</strong>
          </div>
          {!preview.canApply && (
            <div style={{ color: '#b91c1c', fontWeight: 600, fontSize: 12, marginBottom: 6 }}>
              ✗ Blocked — a record to delete has operational data: {preview.blocked.map(b => `#${b.coursesId} ${b.table}(${b.rows})`).join(', ')}. Don't delete a live managed course.
            </div>
          )}
          <div style={{ fontSize: 12, color: '#334155', marginBottom: 4 }}>What would happen:</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
            {preview.plan.map((p, i) => (
              <li key={i} style={{ color: p.action.startsWith('DELETE') ? '#b91c1c' : '#334155' }}>
                <code>{p.table}</code>: {p.action} — <strong>{p.rows}</strong> row{p.rows === 1 ? '' : 's'}
              </li>
            ))}
          </ul>
          {preview.canApply ? (
            <button onClick={doApply} disabled={busy} style={{ ...primaryBtn, marginTop: 10, background: '#b91c1c' }}>
              {busy ? 'Merging…' : 'Apply merge (permanent)'}
            </button>
          ) : (
            <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8' }}>Fix the blocked record before this can be applied.</div>
          )}
        </div>
      )}
      {done && (
        <div style={{ marginTop: 12, padding: 10, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, fontSize: 12, color: '#166534' }}>
          ✓ Merged {done.merged.map(id => '#' + id).join(', ')} into #{done.canonicalId} — deleted {done.coursesDeleted} duplicate record(s) + {done.integrationsDeleted} integration(s). Re-scanning…
        </div>
      )}
    </div>
  )
}

// TTC/ChronoGolf rate config — audit every course for invalid rates and fix one
// from the live catalog. An invalid rate is what makes a booking come back
// "Failed"/"quoteonly" (e.g. The Lakes' stale discounted rate).
function TtcRateToolCard() {
  const [busy, setBusy] = useState('')          // 'audit' | 'load' | 'save'
  const [audit, setAudit] = useState(null)
  const [sel, setSel] = useState(null)          // one course's config + rate catalog
  const [pick, setPick] = useState({ rateCode: '', rateCodeDiscounted: '' })
  const [error, setError] = useState('')
  const [savedMsg, setSavedMsg] = useState('')

  async function runAudit() {
    setBusy('audit'); setError(''); setSel(null); setSavedMsg('')
    try { const { data } = await tournamentApi.get('/admin/data-migration/ttc-rate-audit'); setAudit(data) }
    catch (err) { setError(err?.response?.data?.message || err?.message || 'Audit failed') }
    finally { setBusy('') }
  }
  async function loadCourse(coursesId) {
    setBusy('load'); setError(''); setSavedMsg('')
    try {
      const { data } = await tournamentApi.get('/admin/data-migration/ttc-rate-config', { params: { coursesId } })
      setSel(data); setPick({ rateCode: data.rateCode || '', rateCodeDiscounted: data.rateCodeDiscounted || '' })
    } catch (err) { setError(err?.response?.data?.message || err?.message || 'Load failed') }
    finally { setBusy('') }
  }
  async function save() {
    if (!sel) return
    setBusy('save'); setError(''); setSavedMsg('')
    try {
      await tournamentApi.post('/admin/data-migration/ttc-rate-config', {
        coursesId: sel.course.coursesId, rateCode: pick.rateCode, rateCodeDiscounted: pick.rateCodeDiscounted,
      })
      setSavedMsg('Saved ✓')
      await loadCourse(sel.course.coursesId)   // reload → re-validate the badges
    } catch (err) { setError(err?.response?.data?.message || err?.message || 'Save failed') }
    finally { setBusy('') }
  }

  async function removeIntegration() {
    if (!sel) return
    if (!confirm(`Remove the TTC integration from "${sel.course.courseName}"? It currently books "${sel.ttcName}". This stops it booking the wrong course (reversible). Use this only if it is NOT a TTC course.`)) return
    setBusy('save'); setError(''); setSavedMsg('')
    try {
      await tournamentApi.post('/admin/data-migration/ttc-remove-integration', { coursesId: sel.course.coursesId })
      setSavedMsg('TTC integration removed ✓')
      setSel(null)
      await runAudit()
    } catch (err) { setError(err?.response?.data?.message || err?.message || 'Remove failed') }
    finally { setBusy('') }
  }

  async function changeCrsid(newCrsid) {
    if (!sel || !newCrsid || newCrsid === sel.crsid) return
    if (!confirm(`Point "${sel.course.courseName}" at TTC course "${sel.catalog.find(c => c.courseCode === newCrsid)?.courseName || newCrsid}"? This changes which tee sheet it books, and clears its rates (re-pick them after).`)) return
    setBusy('save'); setError(''); setSavedMsg('')
    try {
      await tournamentApi.post('/admin/data-migration/ttc-rate-config', { coursesId: sel.course.coursesId, crsid: newCrsid })
      setSavedMsg('Course changed ✓ — now pick its rates')
      await loadCourse(sel.course.coursesId)
    } catch (err) { setError(err?.response?.data?.message || err?.message || 'Change failed') }
    finally { setBusy('') }
  }

  const badge = (ok) => <span style={{ fontSize: 11, fontWeight: 700, color: ok ? '#15803d' : '#b91c1c' }}>{ok ? '✓ valid' : '✗ invalid'}</span>
  const mapTag = (c) => {
    if (!c.crsidInCatalog) return <span style={{ color: '#b91c1c', fontWeight: 700 }}>✗ crsid not in TTC</span>
    if (c.ttcName && !c.nameMatch) return <span style={{ color: '#b91c1c', fontWeight: 700 }}>✗ wrong course</span>
    if (c.duplicateCrsid) return <span style={{ color: '#b45309', fontWeight: 700 }}>⚠ shared crsid</span>
    return <span style={{ color: '#15803d' }}>✓</span>
  }

  return (
    <Card
      title="TTC / ChronoGolf course + rate config"
      subtitle="Runs against THIS environment. Flags two problems: a crsid wired to the WRONG TTC course (bookings land at a different course), and rates that aren't in the course's live catalog (bookings fail 'Failed'/'quoteonly'). Fix the course mapping and rates from the catalog — validated before saving."
    >
      <button onClick={runAudit} disabled={!!busy} style={{ ...primaryBtn, marginTop: 0, opacity: busy ? 0.55 : 1 }}>
        {busy === 'audit' ? 'Auditing…' : 'Run audit'}
      </button>
      {error && <p style={errorStyle}>{error}</p>}

      {audit && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 13, marginBottom: 8 }}>
            {audit.problems === 0
              ? <span style={{ color: '#15803d', fontWeight: 600 }}>✓ All {audit.count} TTC courses map correctly with valid rates.</span>
              : <><strong>{audit.count}</strong> courses · <span style={{ color: '#b91c1c', fontWeight: 600 }}>{audit.mappingProblems} mapping problem(s)</span>{audit.soldMappingProblems > 0 && <span style={{ color: '#b91c1c', fontWeight: 700 }}> ({audit.soldMappingProblems} are being sold!)</span>} · {audit.problems} total flagged</>}
          </div>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
            <thead><tr style={{ color: '#64748b', textAlign: 'left' }}>
              <th style={testTh}>Course (local)</th><th style={testTh}>crsid</th><th style={testTh}>Books (TTC)</th><th style={testTh}>Mapping</th><th style={testTh}>Sold</th><th style={testTh}>Booked</th><th style={testTh}>Rates</th><th style={testTh}></th>
            </tr></thead>
            <tbody>
              {audit.courses.map((c) => (
                <tr key={c.coursesId} style={{ background: c.mappingProblem ? '#fef2f2' : (c.rateProblem ? '#fffbeb' : 'transparent') }}>
                  <td style={testTd}>{c.courseName}</td>
                  <td style={testTd}><code style={{ fontSize: 11 }}>{c.crsid}</code></td>
                  <td style={testTd}>{c.ttcName ? <span style={{ color: c.nameMatch ? '#334155' : '#b91c1c' }}>{c.ttcName}</span> : <span style={{ color: '#b91c1c' }}>—</span>}</td>
                  <td style={testTd}>{mapTag(c)}</td>
                  <td style={testTd}>{c.sold ? <span style={{ color: '#b45309', fontWeight: 600 }}>sold</span> : <span style={{ color: '#94a3b8' }}>—</span>}</td>
                  <td style={testTd}>{c.bookedRounds > 0
                    ? <span style={{ color: (c.mappingProblem && c.confirmedRounds > 0) ? '#b91c1c' : '#334155', fontWeight: (c.mappingProblem && c.confirmedRounds > 0) ? 700 : 400 }}>{c.bookedRounds}{c.confirmedRounds > 0 ? ` (${c.confirmedRounds} conf)` : ''}</span>
                    : <span style={{ color: '#94a3b8' }}>—</span>}</td>
                  <td style={testTd}>{c.error ? <span style={{ color: '#b45309' }}>lookup error</span> : <>{badge(c.rateOk)}{c.discSet ? <> / {badge(c.discOk)}</> : ''}</>}</td>
                  <td style={testTd}><button onClick={() => loadCourse(c.coursesId)} disabled={!!busy} style={linkBtn}>Fix →</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {sel && (
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>{sel.course.courseName}</div>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 14 }}>
            Currently books: {sel.ttcName
              ? <><strong style={{ color: sel.nameMatch ? '#15803d' : '#b91c1c' }}>{sel.ttcName}</strong> {sel.nameMatch ? '(matches)' : '(⚠ different course!)'}</>
              : <span style={{ color: '#b91c1c' }}>crsid <code>{sel.crsid}</code> not found in TTC</span>} · teesheet <code>{sel.teesheet}</code>
          </div>

          {/* Other providers on this course + remove — for bogus TTC rows */}
          <div style={{ marginBottom: 16, fontSize: 12, color: '#64748b' }}>
            Other booking integrations: {sel.otherIntegrations && sel.otherIntegrations.length
              ? sel.otherIntegrations.map((o, i) => <span key={i}><code>{o.provider}/{o.category}</code>{o.active ? '' : ' (inactive)'}{i < sel.otherIntegrations.length - 1 ? ', ' : ''}</span>)
              : <span style={{ color: '#94a3b8' }}>none — this course only books via TTC</span>}
            {(!sel.nameMatch || !sel.crsidInCatalog) && (
              <button onClick={removeIntegration} disabled={!!busy} style={{ ...linkBtn, color: '#b91c1c', marginLeft: 12 }}>
                Remove TTC integration
              </button>
            )}
          </div>

          {/* Course mapping (crsid) — the important fix */}
          <div style={{ marginBottom: 16, padding: 12, background: (!sel.nameMatch || !sel.crsidInCatalog) ? '#fef2f2' : '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>Which TTC course does this book? (crsid)</div>
            <select value={sel.crsid || ''} onChange={e => changeCrsid(e.target.value)} disabled={!!busy} style={{ ...testInput, maxWidth: 460 }}>
              {sel.crsid && !sel.catalog.some(c => c.courseCode === sel.crsid) && <option value={sel.crsid}>{sel.crsid} (not in catalog)</option>}
              {sel.catalog.map(c => <option key={c.courseCode} value={c.courseCode}>{c.courseName} — {c.courseCode}</option>)}
            </select>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>Changing this repoints the course to a different tee sheet and clears its rates (re-pick below).</div>
          </div>

          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>{sel.rates.length} rates in this course's catalog</div>

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Customer rate (shown to golfers) {badge(sel.rateCodeValid)}</div>
            {!sel.rateCodeValid && sel.rateCode && <div style={{ fontSize: 11, color: '#b91c1c', marginBottom: 4 }}>Current <code>{sel.rateCode}</code> is not in the catalog.</div>}
            <select value={pick.rateCode} onChange={e => setPick(p => ({ ...p, rateCode: e.target.value }))} style={{ ...testInput, maxWidth: 420 }}>
              <option value="">— None —</option>
              {sel.rates.map(r => <option key={r.rateCode} value={r.rateCode}>{r.rateName}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Discounted / wholesale rate (booked to realize margin) {sel.rateCodeDiscounted ? badge(sel.rateCodeDiscountedValid) : null}</div>
            {!sel.rateCodeDiscountedValid && sel.rateCodeDiscounted && <div style={{ fontSize: 11, color: '#b91c1c', marginBottom: 4 }}>Current <code>{sel.rateCodeDiscounted}</code> is not in the catalog — bookings fall back to the customer rate (no margin).</div>}
            <select value={pick.rateCodeDiscounted} onChange={e => setPick(p => ({ ...p, rateCodeDiscounted: e.target.value }))} style={{ ...testInput, maxWidth: 420 }}>
              <option value="">— None (book at customer rate) —</option>
              {sel.rates.map(r => <option key={r.rateCode} value={r.rateCode}>{r.rateName}</option>)}
            </select>
          </div>

          <button onClick={save} disabled={!!busy} style={{ ...primaryBtn, marginTop: 4, opacity: busy ? 0.55 : 1 }}>
            {busy === 'save' ? 'Saving…' : 'Save rate config'}
          </button>
          {savedMsg && <span style={{ marginLeft: 12, color: '#15803d', fontWeight: 600, fontSize: 13 }}>{savedMsg}</span>}
        </div>
      )}
    </Card>
  )
}

// ─── Underpayment audit (SEC #175 forensic) ──────────────────────────────
//
// Detects whether the pre-fix embed pricing-integrity hole was ever exploited:
// scans direct bookings and flags any paid meaningfully under the server-
// authoritative total. Backed by GET /admin/data-migration/underpaid-audit.
function UnderpaymentAuditCard() {
  const [busy, setBusy] = useState(false)
  const [report, setReport] = useState(null)
  const [error, setError] = useState('')

  async function run() {
    setBusy(true); setError(''); setReport(null)
    try {
      const { data } = await tournamentApi.get('/admin/data-migration/underpaid-audit')
      setReport(data)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Audit failed')
    } finally { setBusy(false) }
  }

  return (
    <Card
      title="Underpayment audit (SEC #175)"
      subtitle="Scans every direct booking (embed + partner API; CTQ excluded) and compares what was actually paid against the server-authoritative price. Flags any trip paid more than $1 under its real total — the signature of the pre-fix pricing-integrity hole. Run on production. Zero flags = never exploited."
    >
      <button onClick={run} disabled={busy} style={primaryBtn}>
        {busy ? 'Scanning…' : 'Run underpayment audit'}
      </button>
      {error && <p style={errorStyle}>{error}</p>}
      {report && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 13, marginBottom: 8 }}>
            {report.flaggedCount === 0
              ? <span style={{ color: '#15803d', fontWeight: 600 }}>✓ Clean — {report.scanned} direct booking(s) scanned, none underpaid.</span>
              : <><strong style={{ color: '#b91c1c' }}>{report.flaggedCount}</strong> of {report.scanned} booking(s) paid under their authoritative price</>}
            {report.errorCount > 0 && <span style={{ color: '#b45309' }}> · {report.errorCount} could not be priced</span>}
          </div>
          {report.flaggedCount > 0 && (
            <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
              <thead><tr style={{ color: '#64748b', textAlign: 'left' }}>
                <th style={testTh}>Booking</th><th style={testTh}>Reseller</th><th style={testTh}>Status</th>
                <th style={testTh}>Email</th><th style={testTh}>Paid</th><th style={testTh}>Expected</th><th style={testTh}>Shortfall</th>
              </tr></thead>
              <tbody>
                {report.flagged.map((f, i) => (
                  <tr key={i}>
                    <td style={testTd}>#{f.bookingId}</td>
                    <td style={testTd}>#{f.resellerAccountId}</td>
                    <td style={testTd}>{f.status}</td>
                    <td style={testTd}>{f.email}</td>
                    <td style={testTd}>{f.currency} ${Number(f.paid).toFixed(2)}</td>
                    <td style={testTd}>{f.currency} ${Number(f.expected).toFixed(2)}</td>
                    <td style={{ ...testTd, color: '#b91c1c', fontWeight: 600 }}>${Number(f.shortfall).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Card>
  )
}

// ─── Negative-margin audit (SEC #177 follow-up) ──────────────────────────
//
// Proactive margin-integrity scan: flags confirmed bookings whose customer
// payment doesn't cover the provider cost InteGolf fronted + remitted tax.
// Backed by GET /admin/data-migration/negative-margin-audit.
function NegativeMarginAuditCard() {
  const [busy, setBusy] = useState(false)
  const [report, setReport] = useState(null)
  const [error, setError] = useState('')

  async function run() {
    setBusy(true); setError(''); setReport(null)
    try {
      const { data } = await tournamentApi.get('/admin/data-migration/negative-margin-audit')
      setReport(data)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Audit failed')
    } finally { setBusy(false) }
  }

  return (
    <Card
      title="Negative-margin audit (SEC #177)"
      subtitle="Scans confirmed bookings and flags any whose customer payment doesn't cover the provider cost InteGolf fronted (Expedia hotel + course fees) plus remitted tax. Mode A underwater = direct InteGolf loss; Mode B = reseller loss (recovered via the vault charge). Catches anything that slips past the #175 gate before the weekly payout batch. Run on production."
    >
      <button onClick={run} disabled={busy} style={primaryBtn}>
        {busy ? 'Scanning…' : 'Run negative-margin audit'}
      </button>
      {error && <p style={errorStyle}>{error}</p>}
      {report && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 13, marginBottom: 8 }}>
            {report.flaggedCount === 0
              ? <span style={{ color: '#15803d', fontWeight: 600 }}>✓ Clean — {report.scanned} confirmed booking(s) scanned, all cover their cost.</span>
              : <><strong style={{ color: '#b91c1c' }}>{report.flaggedCount}</strong> of {report.scanned} booking(s) underwater (payment below cost + tax)</>}
            {report.errorCount > 0 && <span style={{ color: '#b45309' }}> · {report.errorCount} could not be priced</span>}
          </div>
          {report.flaggedCount > 0 && (
            <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
              <thead><tr style={{ color: '#64748b', textAlign: 'left' }}>
                <th style={testTh}>Booking</th><th style={testTh}>Mode</th><th style={testTh}>Status</th>
                <th style={testTh}>Paid</th><th style={testTh}>Provider cost</th><th style={testTh}>Tax</th><th style={testTh}>Margin</th>
              </tr></thead>
              <tbody>
                {report.flagged.map((f, i) => (
                  <tr key={i}>
                    <td style={testTd}>#{f.bookingId}</td>
                    <td style={testTd}>{f.paymentMode}</td>
                    <td style={testTd}>{f.status}</td>
                    <td style={testTd}>{f.currency} ${Number(f.paid).toFixed(2)}</td>
                    <td style={testTd}>{f.currency} ${Number(f.providerCost).toFixed(2)}</td>
                    <td style={testTd}>{f.currency} ${Number(f.tax).toFixed(2)}</td>
                    <td style={{ ...testTd, color: '#b91c1c', fontWeight: 600 }}>${Number(f.margin).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Card>
  )
}

function ReconciliationCard() {
  const [busy, setBusy] = useState('')          // 'load' | 'check' | 'cancel' | ''
  const [report, setReport] = useState(null)
  const [error, setError] = useState('')
  // Itinerary checker
  const [itin, setItin] = useState('')
  const [email, setEmail] = useState('')
  const [itinResult, setItinResult] = useState(null)
  const [itinError, setItinError] = useState('')

  async function load() {
    setBusy('load'); setError(''); setReport(null)
    try {
      const { data } = await tournamentApi.get('/admin/data-migration/reseller-reconcile')
      setReport(data)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Load failed')
    } finally { setBusy('') }
  }

  async function checkItinerary(cancel) {
    if (cancel && !confirm(`Cancel the Expedia room(s) on itinerary ${itin}? This cancels the hotel and refunds the card.`)) return
    setBusy(cancel ? 'cancel' : 'check'); setItinError(''); if (!cancel) setItinResult(null)
    try {
      const { data } = await tournamentApi.post('/admin/data-migration/expedia-itinerary-check', {
        itineraryId: itin, email, cancel: !!cancel,
      })
      setItinResult(data)
    } catch (err) {
      setItinError(err?.response?.data?.message || err?.message || 'Lookup failed')
    } finally { setBusy('') }
  }

  const flagColor = (f) => f === 'money-out-on-dead-trip' ? '#b91c1c' : f === 'hold-open' ? '#b45309' : '#15803d'
  const anyActive = (itinResult?.rooms || []).some(r => r.status === 'booked' || r.cancelable)

  return (
    <Card
      title="Reconciliation — outstanding money + hotel lookup"
      subtitle="Runs against THIS environment's database. On production it shows every reseller payment still holding or charged (real cards only), and lets you look up any Expedia itinerary number from a card statement to see if the room is still live — and cancel it to refund the card."
    >
      <button onClick={load} disabled={!!busy} style={{ ...primaryBtn, marginTop: 0, opacity: busy ? 0.55 : 1 }}>
        {busy === 'load' ? 'Loading…' : 'Load outstanding money'}
      </button>
      {error && <p style={errorStyle}>{error}</p>}

      {report && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 13, color: '#0f172a', marginBottom: 8 }}>
            {report.count === 0
              ? <span style={{ color: '#15803d', fontWeight: 600 }}>✓ Nothing outstanding — every real payment is refunded or voided.</span>
              : <><strong>{report.count}</strong> payment(s) with money out · <span style={{ color: '#b91c1c', fontWeight: 600 }}>{report.needsAttention} need attention</span></>}
          </div>
          {report.count > 0 && (
            <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
              <thead><tr style={{ color: '#64748b', textAlign: 'left' }}>
                <th style={testTh}>Booking</th><th style={testTh}>Trip status</th><th style={testTh}>Golfer</th>
                <th style={testTh}>Amount</th><th style={testTh}>Payment</th><th style={testTh}>Hotel itinerary</th><th style={testTh}>Flag</th>
              </tr></thead>
              <tbody>
                {report.items.map((it, i) => (
                  <tr key={i}>
                    <td style={testTd}>#{it.bookingId}</td>
                    <td style={testTd}>{it.bookingStatus}</td>
                    <td style={testTd}>{it.golfer}</td>
                    <td style={testTd}>{it.currency} ${Number(it.amount).toFixed(2)}</td>
                    <td style={testTd}><span style={{ fontWeight: 600 }}>{it.paymentStatus}</span></td>
                    <td style={testTd}>{it.hotelItinerary ? <code style={{ fontSize: 11 }}>{it.hotelItinerary}</code> : '—'}</td>
                    <td style={{ ...testTd, color: flagColor(it.flag), fontWeight: 600 }}>{it.flag}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 8 }}>Look up a hotel itinerary (from a card statement)</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13, color: '#334155', display: 'flex', flexDirection: 'column', gap: 4 }}>
            Itinerary #
            <input value={itin} onChange={e => setItin(e.target.value.replace(/\D/g, ''))} inputMode="numeric" placeholder="9088719817221" style={{ ...testInp, width: 180 }} />
          </label>
          <label style={{ fontSize: 13, color: '#334155', display: 'flex', flexDirection: 'column', gap: 4 }}>
            Guest email
            <input value={email} onChange={e => setEmail(e.target.value)} placeholder="guest@email.com" style={{ ...testInp, width: 220 }} />
          </label>
          <button onClick={() => checkItinerary(false)} disabled={!itin || !email || !!busy} style={{ ...primaryBtn, marginTop: 0, opacity: (!itin || !email || busy) ? 0.55 : 1 }}>
            {busy === 'check' ? 'Checking…' : 'Check status'}
          </button>
          {anyActive && (
            <button onClick={() => checkItinerary(true)} disabled={!!busy} style={{ marginTop: 0, padding: '10px 18px', fontSize: 14, fontWeight: 600, background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.55 : 1 }}>
              {busy === 'cancel' ? 'Cancelling…' : 'Cancel room(s) + refund'}
            </button>
          )}
        </div>
        {itinError && <p style={errorStyle}>{itinError}</p>}
        {itinResult && (
          <div style={{ marginTop: 12 }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', maxWidth: 800 }}>
              <thead><tr style={{ color: '#64748b', textAlign: 'left' }}>
                <th style={testTh}>Room status</th><th style={testTh}>Guest</th><th style={testTh}>Check-in</th><th style={testTh}>Check-out</th><th style={testTh}>Cancelable</th>
              </tr></thead>
              <tbody>
                {(itinResult.rooms || []).map((r, i) => (
                  <tr key={i}>
                    <td style={{ ...testTd, color: r.status === 'booked' ? '#b91c1c' : '#15803d', fontWeight: 600 }}>{r.status}</td>
                    <td style={testTd}>{r.guest}</td>
                    <td style={testTd}>{r.checkin}</td>
                    <td style={testTd}>{r.checkout}</td>
                    <td style={testTd}>{r.cancelable ? 'yes' : 'no'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {itinResult.cancelResults && (
              <p style={{ fontSize: 12, color: '#334155', marginTop: 8, fontWeight: 600 }}>
                {itinResult.cancelResults.map((c, i) => c.cancelled ? `room ${i + 1}: cancelled ✓ (refund will post)` : `room ${i + 1}: ${c.note || c.error || 'not cancelled'}`).join('  ·  ')}
              </p>
            )}
            {!itinResult.cancelResults && (
              <p style={{ fontSize: 12, color: anyActive ? '#b45309' : '#15803d', marginTop: 8 }}>
                {anyActive ? '⚠ This room is still a live booking — click “Cancel room(s) + refund” to cancel it.' : '✓ Already cancelled at Expedia — any refund is on its way to the card (Expedia posts refunds a few days after cancel).'}
              </p>
            )}
          </div>
        )}
      </div>
    </Card>
  )
}

/**
 * BookingToolsCard — enter a reseller booking ID to (a) inspect where it
 * stopped (trip status, each component's status + provider failure reason,
 * and each payment's gateway status), and (b) release any still-authorized
 * hold whose auto-void failed (voids the pre-auth via a $0.00 completion).
 */
function BookingToolsCard() {
  const [bookingId, setBookingId] = useState('')
  const [quoteRef, setQuoteRef] = useState('') // quote number (Q-1028) or /quote/<token>
  const [busy, setBusy] = useState('')      // 'inspect' | 'release' | ''
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [releaseMsg, setReleaseMsg] = useState('')
  const [provMsg, setProvMsg] = useState('')
  const [chargeMsg, setChargeMsg] = useState('')

  async function charge() {
    const mb = data?.modeBCollection
    if (!mb) return
    if (!confirm(`Charge the reseller's card $${Number(mb.totalToCharge).toFixed(2)} for booking #${bookingId}? This is a LIVE vault charge (hotel all-in + platform fee) and clears pending_ops_action on success.`)) return
    setBusy('charge'); setError(''); setChargeMsg('')
    try {
      const { data: r } = await tournamentApi.post('/admin/data-migration/booking-mode-b-charge', { bookingId: Number(bookingId) })
      const msgs = (r.results || []).map(x => `${x.type}: ${x.ok ? ('charged $' + Number(x.chargeAmount || 0).toFixed(2)) : (x.reason || x.message || 'failed')}`).join('  ·  ')
      setChargeMsg((r.ok ? '✓ ' : '⚠ ') + (msgs || 'nothing charged') + (r.bookingStatus ? ` — status now ${r.bookingStatus}` : ''))
      await inspect()
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Charge failed')
    } finally { setBusy('') }
  }

  async function resetCollection() {
    if (!confirm(`Mark booking #${bookingId} as UN-collected? This clears every "collected" flag so the full bill shows again. It does NOT reverse any real charge — only use it when nothing was actually collected.`)) return
    setBusy('reset'); setError(''); setChargeMsg('')
    try {
      const { data: r } = await tournamentApi.post('/admin/data-migration/booking-reset-collection', { bookingId: Number(bookingId) })
      setChargeMsg('✓ ' + (r.message || 'reset') + (r.warning ? ` — ⚠ ${r.warning}` : ''))
      await inspect()
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Reset failed')
    } finally { setBusy('') }
  }

  async function inspect() {
    setBusy('inspect'); setError(''); setData(null)
    try {
      // Look up by numeric booking id, or (if that's blank) resolve the latest
      // booking for a pasted quote number / URL token.
      const payload = bookingId ? { bookingId: Number(bookingId) } : { quote: quoteRef.trim() }
      const { data } = await tournamentApi.post('/admin/data-migration/booking-inspect', payload)
      setData(data)
      // Pin the resolved numeric id so Release holds / charge act on it.
      if (data?.booking?.ID_NO) setBookingId(String(data.booking.ID_NO))
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Inspect failed')
    } finally { setBusy('') }
  }

  async function release() {
    if (!confirm(`Release (void) all still-authorized holds on booking #${bookingId}? This reverses the pre-auth on the customer's card.`)) return
    setBusy('release'); setError(''); setReleaseMsg('')
    try {
      const { data: r } = await tournamentApi.post('/admin/data-migration/release-holds', { bookingId: Number(bookingId) })
      const msgs = (r.results || []).map(x => `payment #${x.paymentId}: ${x.ok ? 'released ✓' : (x.message || 'failed')}`).join('  ·  ')
      setReleaseMsg(msgs || 'No authorized payments to release.')
      await inspect()
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Release failed')
    } finally { setBusy('') }
  }

  async function releaseProviders() {
    if (!confirm(`Cancel the HOTEL (and any confirmed golf) with the supplier for booking #${bookingId}? Use this when a trip was refunded but the reservation was never released — this calls RateHawk/Expedia to cancel it. It does NOT touch money.`)) return
    setBusy('providers'); setError(''); setProvMsg('')
    try {
      const { data: r } = await tournamentApi.post('/admin/data-migration/release-providers', { bookingId: Number(bookingId) })
      const comps = (r.components || []).map(c => `${c.type}: ${c.status}`).join('  ·  ')
      const notes = (r.opsNotes || []).length ? `  ⚠ ${r.opsNotes.join('; ')}` : ''
      setProvMsg(`${r.ok ? '✓ released' : '⚠ completed with notes'}${r.quoteSynced ? ' · quote marked cancelled' : ''} — ${comps}${notes}`)
      await inspect()
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Release providers failed')
    } finally { setBusy('') }
  }

  const hasAuthorized = (data?.payments || []).some(p => p.status === 'authorized')
  const hasConfirmedHotel = (data?.components || []).some(c => c.type === 'hotel' && c.status === 'confirmed')

  return (
    <Card
      title="Booking diagnostics + release holds"
      subtitle="Inspect a reseller booking — trip status, each component's provider failure reason, and each payment's gateway status. If a booking failed but a pre-auth is still 'authorized', release it to reverse the hold on the customer's card."
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13, color: '#334155', display: 'flex', flexDirection: 'column', gap: 4 }}>
          Booking ID
          <input value={bookingId} onChange={e => { setBookingId(e.target.value.replace(/\D/g, '')); if (e.target.value) setQuoteRef('') }} inputMode="numeric" placeholder="6" style={{ ...testInp, width: 120 }} />
        </label>
        <span style={{ fontSize: 12, color: '#94a3b8', alignSelf: 'center', paddingBottom: 10 }}>or</span>
        <label style={{ fontSize: 13, color: '#334155', display: 'flex', flexDirection: 'column', gap: 4 }}>
          Quote # or URL token
          <input value={quoteRef} onChange={e => { setQuoteRef(e.target.value); if (e.target.value) setBookingId('') }} placeholder="Q-1028 or e88cbaf…" style={{ ...testInp, width: 260 }} />
        </label>
        <button onClick={inspect} disabled={(!bookingId && !quoteRef.trim()) || !!busy} style={{ ...primaryBtn, marginTop: 0, opacity: ((!bookingId && !quoteRef.trim()) || busy) ? 0.55 : 1 }}>
          {busy === 'inspect' ? 'Loading…' : 'Inspect'}
        </button>
        {hasAuthorized && (
          <button onClick={release} disabled={!!busy} style={{ marginTop: 0, padding: '10px 18px', fontSize: 14, fontWeight: 600, background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.55 : 1 }}>
            {busy === 'release' ? 'Releasing…' : 'Release authorized holds'}
          </button>
        )}
        {hasConfirmedHotel && (
          <button onClick={releaseProviders} disabled={!!busy} title="Cancel the hotel/golf with the supplier — for a trip refunded but never released." style={{ marginTop: 0, padding: '10px 18px', fontSize: 14, fontWeight: 600, background: '#b45309', color: '#fff', border: 'none', borderRadius: 6, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.55 : 1 }}>
            {busy === 'providers' ? 'Cancelling…' : 'Cancel hotel with supplier'}
          </button>
        )}
      </div>

      {error && <p style={errorStyle}>{error}</p>}

      {data?.booking && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, color: '#0f172a', marginBottom: 10 }}>
            <strong>#{data.booking.ID_NO}</strong> · {data.booking.PackageName} · {data.booking.GroupingName}
            {' '}· <span style={{ padding: '2px 8px', borderRadius: 999, background: '#f1f5f9', fontSize: 12 }}>{data.booking.Status}</span>
            {' '}· Mode {data.booking.PaymentMode} / {data.booking.GatewayProvider || 'moneris'}
            {!data.booking.HotelPriceCheckHref && <span style={{ marginLeft: 8, color: '#b91c1c', fontSize: 12 }}>⚠ no HotelPriceCheckHref</span>}
          </div>

          <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', margin: '10px 0 4px' }}>Components</div>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', maxWidth: 800 }}>
            <thead><tr style={{ color: '#64748b', textAlign: 'left' }}>
              <th style={testTh}>Type</th><th style={testTh}>Status</th><th style={testTh}>Reason</th><th style={testTh}>Error / confirmation</th>
            </tr></thead>
            <tbody>
              {(data.components || []).map((c, i) => (
                <tr key={i}>
                  <td style={testTd}>{c.type}</td>
                  <td style={testTd}><span style={{ color: /fail|cancel/.test(c.status) ? '#b91c1c' : /confirm/.test(c.status) ? '#15803d' : '#334155' }}>{c.status}</span></td>
                  <td style={testTd}>{c.reason || '—'}</td>
                  <td style={testTd}>{c.error || c.confirmation || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', margin: '14px 0 4px' }}>Payments</div>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', maxWidth: 800 }}>
            <thead><tr style={{ color: '#64748b', textAlign: 'left' }}>
              <th style={testTh}>ID</th><th style={testTh}>Status</th><th style={testTh}>Amount</th><th style={testTh}>Gateway</th><th style={testTh}>Txn ID</th>
            </tr></thead>
            <tbody>
              {(data.payments || []).map((p, i) => (
                <tr key={i}>
                  <td style={testTd}>{p.id}</td>
                  <td style={testTd}><span style={{ color: p.status === 'authorized' ? '#b45309' : p.status === 'voided' ? '#15803d' : p.status === 'captured' ? '#15803d' : '#334155', fontWeight: 600 }}>{p.status}</span></td>
                  <td style={testTd}>{p.currency} ${Number(p.amount).toFixed(2)}</td>
                  <td style={testTd}>{p.gateway} / {p.mode}</td>
                  <td style={testTd}><code style={{ fontSize: 11 }}>{p.transactionId || '—'}</code></td>
                </tr>
              ))}
            </tbody>
          </table>
          {hasAuthorized && (
            <p style={{ fontSize: 12, color: '#b45309', marginTop: 8 }}>
              ⚠ A payment is still <strong>authorized</strong> — the hold is live on the card. Click “Release authorized holds” to reverse it.
            </p>
          )}
          {releaseMsg && (
            <p style={{ fontSize: 12, color: '#334155', marginTop: 8, fontWeight: 600 }}>{releaseMsg}</p>
          )}
          {hasConfirmedHotel && (
            <p style={{ fontSize: 12, color: '#b45309', marginTop: 8 }}>
              ⚠ A hotel component is still <strong>confirmed</strong> — if this trip was refunded/cancelled, the reservation is still live with the supplier. Click “Cancel hotel with supplier” to release it.
            </p>
          )}
          {provMsg && (
            <p style={{ fontSize: 12, color: '#334155', marginTop: 8, fontWeight: 600 }}>{provMsg}</p>
          )}

          {data.golfRounds?.length > 0 && (
            <div style={{ marginTop: 16, border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, background: '#fff' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Golf rounds — who pays the course</div>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>InteGolf-pays courses (WhoPaysCourse=integolf) are billed to the reseller in Mode B (green fee + 3.49% surcharge). Reseller / Tee-Time courses are settled by the reseller directly and are not billed.</div>
              <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', maxWidth: 820 }}>
                <thead><tr style={{ color: '#64748b', textAlign: 'left' }}>
                  <th style={testTh}>Course</th><th style={testTh}>Provider</th><th style={testTh}>Conf</th><th style={testTh}>$/golfer</th><th style={testTh}>Who pays the course</th>
                </tr></thead>
                <tbody>
                  {data.golfRounds.map((r, i) => (
                    <tr key={i}>
                      <td style={testTd}>{r.course}</td>
                      <td style={testTd}>{r.provider || '—'}</td>
                      <td style={testTd}>{r.confirmation || '—'}</td>
                      <td style={testTd}>{r.pricePerGolfer != null ? `$${Number(r.pricePerGolfer).toFixed(2)}` : '—'}</td>
                      <td style={{ ...testTd, fontWeight: 700, color: r.integolfPays ? '#b91c1c' : '#0f766e' }}>
                        {r.integolfPays ? 'InteGolf pays → bill reseller' : `${r.whoPaysCourse} pays direct`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data.modeBCollection && (
            <div style={{ marginTop: 16, border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, background: '#fff' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Mode-B reseller collection</div>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>What InteGolf bills the reseller's card: the hotel (all-in) + any InteGolf-pays rounds (green fee + markup) + the platform fee on gross (capped at the platform-fee rate). Reseller / Tee-Time rounds are settled by the reseller directly and are not billed.</div>
              <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', maxWidth: 820 }}>
                <thead><tr style={{ color: '#64748b', textAlign: 'left' }}>
                  <th style={testTh}>Line</th><th style={testTh}>Cost</th><th style={testTh}>Markup</th><th style={testTh}>Charge</th><th style={testTh}>Status</th>
                </tr></thead>
                <tbody>
                  {data.modeBCollection.lines.map((l, i) => (
                    <tr key={i} style={l.resellerPaid ? { color: '#94a3b8' } : undefined}>
                      <td style={testTd}>
                        {l.kind === 'fee'
                          ? `platform fee (${(l.pct * 100).toFixed(2)}% of $${Number(l.base).toFixed(2)})${l.capped ? ' — capped' : ''}`
                          : l.kind === 'round' ? `${l.type}${l.course ? ` — ${l.course}` : ''}` : l.type}
                        {l.expedia && <span style={{ color: '#94a3b8' }}> (Expedia incl ${Number(l.expedia.inclusive).toFixed(2)}, mkt-fee ${Number(l.expedia.marketingFee).toFixed(2)})</span>}
                        {l.missingCost && <span style={{ color: '#b91c1c' }}> ⚠ no cost</span>}
                      </td>
                      <td style={testTd}>{l.cost != null ? `$${Number(l.cost).toFixed(2)}` : '—'}</td>
                      <td style={testTd}>{l.markup != null ? `$${Number(l.markup).toFixed(2)}` : '—'}</td>
                      <td style={{ ...testTd, fontWeight: 700 }}>{l.resellerPaid ? '—' : `$${Number(l.charge).toFixed(2)}`}</td>
                      <td style={testTd}>
                        {l.resellerPaid ? 'reseller pays course'
                          : l.settled ? '✓ collected'
                          : (l.kind === 'fee' || l.status === 'confirmed') ? 'outstanding'
                          : l.status}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: 12, color: '#334155', marginTop: 8, lineHeight: 1.7 }}>
                Customer paid reseller <strong>${Number(data.modeBCollection.gross).toFixed(2)}</strong> ·
                To charge reseller now <strong>${Number(data.modeBCollection.totalToCharge).toFixed(2)}</strong>
                {data.modeBCollection.alreadyCollected > 0 && <> · Already collected ${Number(data.modeBCollection.alreadyCollected).toFixed(2)}</>} ·
                Reseller pays courses ≈ ${Number(data.modeBCollection.courseFeesApprox).toFixed(2)} directly ·
                Reseller keeps ≈ ${Number(data.modeBCollection.resellerKeepsApprox).toFixed(2)} ·
                platform fee {(data.modeBCollection.platformFeePct * 100).toFixed(2)}%
              </div>
              {data.modeBCollection.anyMissingCost && <p style={{ fontSize: 12, color: '#b91c1c', marginTop: 6 }}>⚠ A confirmed hotel has no recorded cost — run the hotel-cost backfill on the Sales page first.</p>}
              {!data.modeBCollection.hasVaultCard && <p style={{ fontSize: 12, color: '#b91c1c', marginTop: 6 }}>⚠ No active card on file for account #{data.modeBCollection.cardAccountId} — the reseller must add one (Payment setup) before this can be charged.</p>}
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
                <button onClick={charge} disabled={!data.modeBCollection.canCharge || !!busy}
                  style={{ padding: '10px 18px', fontSize: 14, fontWeight: 700, background: data.modeBCollection.canCharge ? '#0f172a' : '#cbd5e1', color: '#fff', border: 'none', borderRadius: 6, cursor: (data.modeBCollection.canCharge && !busy) ? 'pointer' : 'not-allowed' }}>
                  {busy === 'charge' ? 'Charging…' : `Charge reseller $${Number(data.modeBCollection.totalToCharge).toFixed(2)}`}
                </button>
                {data.modeBCollection.alreadyCollected > 0 && (
                  <button onClick={resetCollection} disabled={!!busy}
                    style={{ padding: '10px 16px', fontSize: 13, fontWeight: 600, background: '#fff', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: 6, cursor: busy ? 'not-allowed' : 'pointer' }}>
                    {busy === 'reset' ? 'Resetting…' : 'Reset collection (mark uncollected)'}
                  </button>
                )}
              </div>
              {chargeMsg && <p style={{ fontSize: 12, color: '#334155', marginTop: 8, fontWeight: 600 }}>{chargeMsg}</p>}
            </div>
          )}

          {data.packageFees && (
            <div style={{ marginTop: 16, border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, background: '#fff' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Package fee breakdown</div>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
                How the reseller's package priced out{data.packageFees.applicable ? ' — and what InteGolf should bill in Mode B, where the reseller collects everything and pays the courses directly (InteGolf bills hotel all-in + 5% of total paid).' : '.'}
              </div>

              {data.packageFees.package && (
                <div style={{ fontSize: 12, color: '#334155', lineHeight: 1.7, marginBottom: 10 }}>
                  <strong>{data.packageFees.package.name}</strong> ·
                  Package price <strong>${Number(data.packageFees.package.price).toFixed(2)}</strong> ·
                  Hotel budget ${Number(data.packageFees.package.maxHotelPricePerNight).toFixed(2)}/night ·
                  Golf cap ${Number(data.packageFees.package.defaultMaxGolfPrice).toFixed(2)}
                  {data.packageFees.package.feeOverrideValue != null && <> · Fee override: {data.packageFees.package.feeOverrideType || 'value'} {data.packageFees.package.feeOverrideValue}</>}
                </div>
              )}

              {data.packageFees.applicable ? (
                <>
                  <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', maxWidth: 820 }}>
                    <thead><tr style={{ color: '#64748b', textAlign: 'left' }}>
                      <th style={testTh}>Billing model</th><th style={testTh}>Hotel (all-in)</th><th style={testTh}>Green fee</th><th style={testTh}>Golf markup</th><th style={testTh}>5% fee</th><th style={testTh}>Charge to reseller</th>
                    </tr></thead>
                    <tbody>
                      <tr style={{ background: '#f0fdf4' }}>
                        <td style={{ ...testTd, fontWeight: 700, color: '#166534' }}>Correct (your rule)</td>
                        <td style={testTd}>${Number(data.packageFees.correctModeBBill.hotelAllIn).toFixed(2)}</td>
                        <td style={testTd}>—</td>
                        <td style={testTd}>—</td>
                        <td style={testTd}>${Number(data.packageFees.correctModeBBill.platformFee).toFixed(2)}</td>
                        <td style={{ ...testTd, fontWeight: 700, color: '#166534' }}>${Number(data.packageFees.correctModeBBill.total).toFixed(2)}</td>
                      </tr>
                      <tr style={{ background: '#fef2f2' }}>
                        <td style={{ ...testTd, fontWeight: 700, color: '#991b1b' }}>Current (billed now)</td>
                        <td style={testTd}>${Number(data.packageFees.currentModeBBill.hotelAllIn).toFixed(2)}</td>
                        <td style={testTd}>${Number(data.packageFees.currentModeBBill.greenFee).toFixed(2)}</td>
                        <td style={testTd}>${Number(data.packageFees.currentModeBBill.golfMarkup).toFixed(2)}</td>
                        <td style={testTd}>${Number(data.packageFees.currentModeBBill.roundCommission).toFixed(2)}</td>
                        <td style={{ ...testTd, fontWeight: 700, color: '#991b1b' }}>${Number(data.packageFees.currentModeBBill.total).toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div style={{ fontSize: 12, color: '#334155', marginTop: 8, lineHeight: 1.7 }}>
                    5% fee base = total customer paid <strong>${Number(data.packageFees.totalCustomerPaid).toFixed(2)}</strong> →
                    <strong> ${Number(data.packageFees.platformFee.amount).toFixed(2)}</strong>
                    {data.packageFees.overchargeVsCorrect !== 0 && (
                      <> · <span style={{ color: '#991b1b', fontWeight: 700 }}>Overcharge vs correct: ${Number(data.packageFees.overchargeVsCorrect).toFixed(2)}</span> (the green fee the reseller already settles with the course)</>
                    )}
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 12, color: '#64748b' }}>Mode A — InteGolf is the merchant of record, so there's no reseller collection.</div>
              )}
            </div>
          )}

          {data.packageConfig && (
            <details style={{ marginTop: 16 }} open>
              <summary style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', cursor: 'pointer' }}>Package configuration (what the reseller set up)</summary>
              <div style={{ marginTop: 8, border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, background: '#fff' }}>
                <div style={{ fontSize: 12, color: '#334155', lineHeight: 1.9 }}>
                  <strong>{data.packageConfig.package.name}</strong> ({data.packageConfig.package.status})
                  {data.packageConfig.paymentMode && <> · Payment mode <strong>{data.packageConfig.paymentMode}</strong></>}<br />
                  {data.packageConfig.package.minGolfers}–{data.packageConfig.package.maxGolfers} golfers ·
                  {' '}{data.packageConfig.package.nights} night(s) · {data.packageConfig.package.roomCount} room(s) ·
                  {' '}{data.packageConfig.package.roundsPerGolfer} rounds/golfer<br />
                  Package price <strong>${Number(data.packageConfig.package.price).toFixed(2)}</strong> ·
                  Hotel budget ${Number(data.packageConfig.package.maxHotelPricePerNight).toFixed(2)}/night ·
                  Golf cap ${Number(data.packageConfig.package.defaultMaxGolfPrice).toFixed(2)}
                  {data.packageConfig.package.feeOverrideValue != null && <> · Fee override: {data.packageConfig.package.feeOverrideType || 'value'} {data.packageConfig.package.feeOverrideValue}</>}
                  {data.packageConfig.package.allowSplitPayments && <> · Split pay</>}
                  {data.packageConfig.package.allowInstallments && <> · Installments ({data.packageConfig.package.installmentCount})</>}
                </div>
                {data.packageConfig.package.description && <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>{data.packageConfig.package.description}</div>}

                {data.packageConfig.courses.length > 0 && (
                  <>
                    <div style={{ fontSize: 12, fontWeight: 700, marginTop: 12, color: '#0f172a' }}>Courses</div>
                    <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', maxWidth: 820 }}>
                      <thead><tr style={{ color: '#64748b', textAlign: 'left' }}>
                        <th style={testTh}>Course</th><th style={testTh}>City</th><th style={testTh}>Rounds (min–max)</th><th style={testTh}>Price cap</th><th style={testTh}>Required</th>
                      </tr></thead>
                      <tbody>
                        {data.packageConfig.courses.map((c, i) => (
                          <tr key={i} style={c.excluded ? { color: '#94a3b8', textDecoration: 'line-through' } : undefined}>
                            <td style={testTd}>{c.name}{c.excluded && <span style={{ textDecoration: 'none' }}> (excluded)</span>}</td>
                            <td style={testTd}>{c.city || '—'}</td>
                            <td style={testTd}>{c.minRounds}–{c.maxRounds}</td>
                            <td style={testTd}>{c.maxPricePerRound != null ? `$${Number(c.maxPricePerRound).toFixed(2)}${c.hardCap ? ' (hard)' : ''}` : '—'}</td>
                            <td style={testTd}>{c.isRequired ? 'Yes' : 'No'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                {data.packageConfig.addons.length > 0 && (
                  <>
                    <div style={{ fontSize: 12, fontWeight: 700, marginTop: 12, color: '#0f172a' }}>Add-ons</div>
                    <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', maxWidth: 820 }}>
                      <thead><tr style={{ color: '#64748b', textAlign: 'left' }}>
                        <th style={testTh}>Add-on</th><th style={testTh}>Per golfer</th><th style={testTh}>Per package</th><th style={testTh}>Optional</th>
                      </tr></thead>
                      <tbody>
                        {data.packageConfig.addons.map((a, i) => (
                          <tr key={i}>
                            <td style={testTd}>{a.name}</td>
                            <td style={testTd}>{a.pricePerGolfer != null ? `$${Number(a.pricePerGolfer).toFixed(2)}` : '—'}</td>
                            <td style={testTd}>{a.pricePerPackage != null ? `$${Number(a.pricePerPackage).toFixed(2)}` : '—'}</td>
                            <td style={testTd}>{a.isOptional ? 'Yes' : 'No'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                {data.packageConfig.refundTiers.length > 0 && (
                  <div style={{ fontSize: 12, color: '#334155', marginTop: 12 }}>
                    <span style={{ fontWeight: 700, color: '#0f172a' }}>Refund policy:</span>{' '}
                    {data.packageConfig.refundTiers.map((t, i) => (
                      <span key={i}>{i > 0 && ' · '}{t.refundPercent}% if {t.daysBeforeMax != null ? `${t.daysBeforeMin}–${t.daysBeforeMax}` : `${t.daysBeforeMin}+`} days before</span>
                    ))}
                  </div>
                )}
              </div>
            </details>
          )}

          <details style={{ marginTop: 10 }}>
            <summary style={{ fontSize: 12, color: '#64748b', cursor: 'pointer' }}>Raw provider + gateway responses (why a component failed, order/txn ids)</summary>
            <pre style={{ fontSize: 11, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: 10, overflow: 'auto', maxHeight: 360 }}>
{JSON.stringify({
  components: (data.components || []).map(c => ({ type: c.type, status: c.status, reason: c.reason, error: c.error, raw: c.raw })),
  payments: (data.payments || []).map(p => ({ id: p.id, status: p.status, orderId: p.orderId, transactionId: p.transactionId, gatewayResponse: p.gatewayResponse })),
}, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </Card>
  )
}

// ─── Backfill package courses (existing prod packages with 0 wires) ──────

/**
 * Editor's old UX showed a package's available grouping courses as toggle
 * candidates -- resellers thought they were 'in the package' but until
 * Save was clicked, reseller_package_courses had 0 rows. Embed then
 * rendered Add round as a no-op. This button wires all grouping courses
 * into any package that currently has 0 -- idempotent (only touches
 * empty packages). Pairs with the auto-save UX in the editor going
 * forward.
 */
function BackfillPackageCoursesCard() {
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')

  async function run() {
    if (!window.confirm('For every package with 0 wired courses, wire all of its grouping\'s available courses?\nIdempotent — packages with wired courses are skipped.')) return
    setBusy(true); setError(''); setResult(null)
    try {
      // Endpoint lives on tournament-api under /portal/reseller/packages.
      // Goes through the proxy via tournamentApi.
      const { data } = await tournamentApi.post('/portal/reseller/packages/_admin/backfill-package-courses', {})
      setResult(data)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Backfill failed')
    } finally { setBusy(false) }
  }

  return (
    <Card
      title="Backfill empty packages with grouping courses"
      subtitle="For every reseller package that currently has zero wired courses, wire all of its grouping's available courses (those inside the grouping radius, not excluded). Fixes packages where the editor's old candidate-picker UX left courses unsaved. Idempotent."
    >
      <button onClick={run} disabled={busy} style={primaryBtn}>
        {busy ? 'Backfilling…' : 'Backfill empty packages'}
      </button>
      {error && <p style={errorStyle}>{error}</p>}
      {result && (
        <div style={{ marginTop: 16, padding: 12, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, fontSize: 13 }}>
          <div style={{ fontWeight: 600, color: '#15803d', marginBottom: 4 }}>
            ✓ Scanned {result.packagesScanned} empty package{result.packagesScanned === 1 ? '' : 's'} · wired {result.packagesWired} · {result.totalCoursesWired} course rows inserted
          </div>
          {result.results?.length > 0 && (
            <ul style={{ marginTop: 8, paddingLeft: 18, fontSize: 12, color: '#334155', maxHeight: 240, overflowY: 'auto' }}>
              {result.results.map((r, i) => (
                <li key={i}>
                  <code>#{r.packageId}</code> {r.packageName} —{' '}
                  {r.wiredCount > 0
                    ? <b style={{ color: '#15803d' }}>wired {r.wiredCount}</b>
                    : <span style={{ color: '#b45309' }}>skipped: {r.reason}</span>
                  }
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  )
}

// ─── Backfill integration_providers from course_integolf_products ─────────

/**
 * Today's InteGolf JSON imports created cip rows (which the booking router
 * reads) but not ip rows (which the admin Course Integrations page reads).
 * Result: 19 courses booked fine but showed as "(no integration)" on
 * /admin/course-integrations. This button mirrors every cip row's adapter
 * + credentials into an ip row so the admin screen sees the same picture.
 * The new importer does this inline; the button is for one-off backfill
 * of pre-patch imports. Idempotent.
 */
function BackfillIpFromCipCard() {
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')

  async function run() {
    setBusy(true); setError(''); setResult(null)
    try {
      const { data } = await migPost('/admin/data-migration/backfill-ip-from-cip', {})
      setResult(data)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Backfill failed')
    } finally { setBusy(false) }
  }

  return (
    <Card
      title="Backfill course integrations admin view"
      subtitle="Makes every course imported via the InteGolf JSON tool show up on /admin/course-integrations. Mirrors course_integolf_products.Settings into integration_providers so the admin screen and the booking router see the same picture. Idempotent — safe to click anytime."
    >
      <button onClick={run} disabled={busy} style={primaryBtn}>
        {busy ? 'Backfilling…' : 'Backfill ip rows from cip'}
      </button>
      {error && <p style={errorStyle}>{error}</p>}
      {result && (
        <div style={{ marginTop: 16, padding: 12, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8 }}>
          <div style={{ fontWeight: 600, color: '#15803d', marginBottom: 6 }}>
            ✓ Created {result.insertedCount} · updated {result.updatedCount} · skipped {result.skippedCount}{result.errorCount > 0 ? ` · errors ${result.errorCount}` : ''}
          </div>
          {result.inserted?.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, color: '#475569', marginBottom: 4 }}>Newly visible on /admin/course-integrations:</div>
              <ul style={{ paddingLeft: 18, fontSize: 12, color: '#334155', maxHeight: 200, overflowY: 'auto' }}>
                {result.inserted.map((r, i) => (
                  <li key={i}><code>#{r.courseId}</code> {r.courseName} <span style={{ color: '#64748b' }}>({r.adapter})</span></li>
                ))}
              </ul>
            </div>
          )}
          {result.errors?.length > 0 && (
            <ul style={{ marginTop: 8, paddingLeft: 18, fontSize: 12, color: '#b91c1c', maxHeight: 200, overflowY: 'auto' }}>
              {result.errors.map((r, i) => (
                <li key={i}><code>#{r.courseId}</code> {r.courseName} — {r.error}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  )
}

// ─── API-only courses sync (direct dev->prod, no file upload) ────────────

/**
 * One-click compare + sync for IsApiOnly=1 catalog courses. Operator pastes
 * the source env's API host (e.g. https://stayplayapi-dev2.integolf.com),
 * clicks Compare -> sees per-course diff (location, integration_providers,
 * course_integolf_products) -> clicks Apply to mirror dev's state onto this
 * env. Only touches IsApiOnly=1 rows; never touches signup-managed courses.
 */
function ApiOnlyCoursesSyncCard() {
  // Auto-correct the legacy 'stayplayapi-dev2' typo that crept into localStorage
  // (the right host on localhost is 'stayplay-dev2' -- same hostname for SPA + API).
  const [sourceUrl, setSourceUrl] = useState(() => {
    const v = localStorage.getItem('admin_dm_source_url') || ''
    return v.replace(/stayplayapi-dev2/g, 'stayplay-dev2')
  })
  const [pastedJson, setPastedJson] = useState('')
  const [busy, setBusy]           = useState(false)
  const [report, setReport]       = useState(null)
  const [error, setError]         = useState('')

  function saveSourceUrl(v) { setSourceUrl(v); localStorage.setItem('admin_dm_source_url', v || '') }

  // Order: (1) pasted JSON wins if present. (2) Otherwise try BROWSER fetch
  // of source URL -- the browser can reach the AWS tunnel, prod's server
  // can't. (3) If browser fetch also fails (CORS on source side), surface
  // a clear error.
  async function sync() {
    if (!window.confirm('Auto-sync ALL matched API-only courses to this env?\n\n' +
      'Matches by CourseName. Auto-updates location + integration credentials. Duplicates and unmatched courses will be listed in the report, not touched.')) return
    setBusy(true); setError(''); setReport(null)
    try {
      let sourceSnapshot = null
      if (pastedJson.trim()) {
        try { sourceSnapshot = JSON.parse(pastedJson) }
        catch (e) { throw new Error('Pasted JSON is not valid: ' + e.message) }
      } else if (sourceUrl) {
        const url = sourceUrl.replace(/\/+$/, '') + '/api/play-stay-portal/admin/data-migration/api-only-courses-state'
        const token = localStorage.getItem('portal_token') || localStorage.getItem('token') || ''
        const r = await fetch(url, {
          headers: { 'Authorization': token ? `Bearer ${token}` : '', 'Accept': 'application/json' },
        })
        if (!r.ok) {
          const text = await r.text()
          throw new Error(`Source returned HTTP ${r.status}: ${text.slice(0, 200)}`)
        }
        sourceSnapshot = await r.json()
      } else {
        throw new Error('Provide a Source URL OR paste the snapshot JSON.')
      }
      const { data } = await migPost('/admin/data-migration/auto-sync-api-only-from-source', { sourceSnapshot })
      setReport(data)
    } catch (err) {
      setError(
        err?.response?.data?.message
        || err?.message
        || 'Sync failed. If the browser fetch is hitting CORS, paste the snapshot JSON below instead.'
      )
    } finally { setBusy(false) }
  }

  return (
    <Card
      title="Auto-sync API-only courses from source"
      subtitle="One-click sync. This env's server calls the source env directly (no browser, no upload), iterates every IsApiOnly=1 course, matches by CourseName, and auto-updates location + integration credentials wherever there's a single clean match. Returns a report listing courses that DIDN'T auto-sync (no match, duplicates on either side, name collides with a signup-managed local row) so you can handle them by hand."
    >
      <label style={{ display: 'block', fontSize: 13, color: '#475569', marginBottom: 6 }}>
        Source URL (e.g. <code>https://stayplay-dev2.integolf.com</code>)
      </label>
      <input
        value={sourceUrl}
        onChange={e => saveSourceUrl(e.target.value)}
        placeholder="https://stayplay-dev2.integolf.com"
        style={{ width: '100%', maxWidth: 520, padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, marginBottom: 8 }}
      />

      <details style={{ marginBottom: 12, fontSize: 12, color: '#475569' }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
          Or paste the snapshot JSON (bulletproof — bypasses CORS/cert issues)
        </summary>
        <div style={{ marginTop: 8 }}>
          <ol style={{ paddingLeft: 18, marginTop: 0, marginBottom: 8, lineHeight: 1.6 }}>
            <li>Open localhost admin in a new tab; F12 console.</li>
            <li>Run: <code>fetch('/api/play-stay-portal/admin/data-migration/api-only-courses-state', {`{headers:{Authorization:'Bearer '+(localStorage.portal_token||localStorage.token)}}`}).then(r=>r.text()).then(t=>{`{copy(t)}`})</code></li>
            <li>Paste the JSON below and click Sync now.</li>
          </ol>
          <textarea
            value={pastedJson}
            onChange={e => setPastedJson(e.target.value)}
            placeholder='{"sourceDb": "...", "generatedAt": "...", "courses": [...]}'
            rows={6}
            style={{ width: '100%', maxWidth: 720, padding: 8, border: '1px solid #cbd5e1', borderRadius: 6, fontFamily: 'monospace', fontSize: 11 }}
          />
        </div>
      </details>

      <button onClick={sync} disabled={busy || (!sourceUrl && !pastedJson.trim())} style={{ ...primaryBtn, background: '#dc2626' }}>
        {busy ? 'Syncing…' : 'Sync now'}
      </button>
      {error && <p style={errorStyle}>{error}</p>}
      {report && <SyncReport report={report} />}
    </Card>
  )
}

/**
 * "Push" direction: this env (typically localhost) is the source of truth
 * and pushes its IsApiOnly snapshot to a target env (typically prod).
 * Avoids every cross-origin / cert / outbound-from-prod problem because
 * the data flows FROM localhost TO prod -- the same direction resellers
 * use to hit our public APIs.
 */
function PushApiOnlyToTargetCard() {
  const [targetUrl, setTargetUrl] = useState(() => localStorage.getItem('admin_dm_target_url') || 'https://stayplayapi.integolf.com')
  const [targetToken, setTargetToken] = useState('')
  const [busy, setBusy]   = useState(false)
  const [report, setReport] = useState(null)
  const [error, setError] = useState('')

  function saveTargetUrl(v) { setTargetUrl(v); localStorage.setItem('admin_dm_target_url', v || '') }

  async function push() {
    if (!window.confirm('Push this env\'s API-only catalog to ' + targetUrl + '?\n\n' +
      'This env\'s server will POST its IsApiOnly snapshot to the target. The target diffs against its own state, auto-applies matched updates, returns a report.')) return
    setBusy(true); setError(''); setReport(null)
    try {
      const { data } = await migPost('/admin/data-migration/push-api-only-to-target', {
        targetUrl,
        targetToken: targetToken.trim() || undefined,
      })
      setReport(data)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Push failed')
    } finally { setBusy(false) }
  }

  return (
    <Card
      title="Push API-only courses TO a target environment"
      subtitle="Run this on localhost to push your local catalog state to prod (or any reachable env). This env's server takes its own IsApiOnly snapshot, POSTs it to the target's auto-sync endpoint with your auth, and returns the target's report. Same direction resellers use -- localhost->prod, not prod->localhost -- so it avoids every outbound-from-prod / cert / CORS issue."
    >
      <label style={{ display: 'block', fontSize: 13, color: '#475569', marginBottom: 6 }}>
        Target URL (the prod API host)
      </label>
      <input
        value={targetUrl}
        onChange={e => saveTargetUrl(e.target.value)}
        placeholder="https://stayplayapi.integolf.com"
        style={{ width: '100%', maxWidth: 520, padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, marginBottom: 12 }}
      />

      <label style={{ display: 'block', fontSize: 13, color: '#475569', marginBottom: 6 }}>
        Target JWT (paste from prod tab — required when JWT secrets differ across envs)
      </label>
      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>
        On the prod tab, run in console: <code>copy(localStorage.portal_token || localStorage.token)</code>, then paste below.
      </div>
      <input
        type="password"
        value={targetToken}
        onChange={e => setTargetToken(e.target.value)}
        placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
        style={{ width: '100%', maxWidth: 720, padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, fontFamily: 'monospace', marginBottom: 12 }}
      />

      <button onClick={push} disabled={busy || !targetUrl} style={{ ...primaryBtn, background: '#dc2626' }}>
        {busy ? 'Pushing…' : 'Push to target'}
      </button>
      {error && <p style={errorStyle}>{error}</p>}
      {report && <SyncReport report={report} />}
    </Card>
  )
}

function SyncReport({ report }) {
  const numUpdated   = report.updated?.length || 0
  const numUnchanged = report.unchanged?.length || 0
  const numMissL     = report.unmatchedOnLocal?.length || 0
  const numMissS     = report.unmatchedOnSource?.length || 0
  const numDup       = report.duplicates?.length || 0
  const numBlocked   = report.blocked?.length || 0
  const numErr       = report.errors?.length || 0

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ padding: 12, background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 8, marginBottom: 12, fontSize: 13, color: '#0f172a' }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>
          Source: <code>{report.sourceDb}</code> · {report.sourceCourseCount} courses · Local: {report.localCourseCount} courses
        </div>
        {report.globalProvidersSynced > 0 && (
          <div style={{ fontSize: 12, color: '#15803d', marginBottom: 4 }}>
            🔑 {report.globalProvidersSynced} global provider config{report.globalProvidersSynced === 1 ? '' : 's'} synced (e.g. teetime-com apiKey)
          </div>
        )}
        <div>
          <b style={{ color: '#15803d' }}>{numUpdated}</b> updated ·{' '}
          <b style={{ color: '#64748b' }}>{numUnchanged}</b> unchanged ·{' '}
          <b style={{ color: '#1e40af' }}>{numMissL}</b> on source only ·{' '}
          <b style={{ color: '#64748b' }}>{numMissS}</b> on local only ·{' '}
          <b style={{ color: '#b45309' }}>{numDup}</b> duplicates ·{' '}
          <b style={{ color: '#b91c1c' }}>{numBlocked}</b> blocked ·{' '}
          <b style={{ color: '#b91c1c' }}>{numErr}</b> errors
        </div>
      </div>

      <ReportSection title={`✓ Updated (${numUpdated})`} items={report.updated} color="#15803d"
        render={u => (
          <span>
            <code>#{u.courseId}</code> {u.courseName}
            {u.locationChanged && <span style={{ color: '#475569', marginLeft: 6 }}>📍 location</span>}
            {u.ipUpserts?.length > 0 && <span style={{ color: '#475569', marginLeft: 6 }}>🔌 {u.ipUpserts.join(', ')}</span>}
            {u.ipDeactivations?.length > 0 && <span style={{ color: '#b91c1c', marginLeft: 6 }}>⊘ {u.ipDeactivations.join(', ')}</span>}
            {u.cipUpserts?.length > 0 && <span style={{ color: '#475569', marginLeft: 6 }}>📦 cip {u.cipUpserts.join(',')}</span>}
          </span>
        )} />

      <ReportSection title={`On source only — missing on local (${numMissL})`} items={report.unmatchedOnLocal} color="#1e40af"
        render={u => <code>{u.courseName}</code>}
        note="These exist on the source but not on this env. To add them, use the Import InteGolf JSON tool with the appropriate entries." />

      <ReportSection title={`On local only — missing from source (${numMissS})`} items={report.unmatchedOnSource} color="#64748b"
        render={u => <span><code>#{u.courseId}</code> {u.courseName}</span>}
        note="These are IsApiOnly=1 here but absent from the source. Either source is behind, or they should be cleaned up here." />

      <ReportSection title={`⚠ Duplicates — name appears more than once (${numDup})`} items={report.duplicates} color="#b45309"
        render={u => <span><code>#{u.courseId}</code> {u.courseName} <span style={{ color: '#64748b' }}>(dup on: {u.duplicatedOn})</span></span>}
        note="Auto-update skipped because the name isn't unique. Decide which row wins, then sync again." />

      <ReportSection title={`🚫 Blocked — name collides with signup-managed course (${numBlocked})`} items={report.blocked} color="#b91c1c"
        render={u => <span><code>#{u.courseId}</code> {u.courseName} — <i>{u.reason}</i></span>}
        note="The local row exists but is NOT IsApiOnly. We won't touch it to protect signup-managed data." />

      <ReportSection title={`Errors (${numErr})`} items={report.errors} color="#b91c1c"
        render={u => <span><code>{u.courseName}</code>: {u.error}</span>} />
    </div>
  )
}

function ReportSection({ title, items, color, render, note }) {
  if (!items || items.length === 0) return null
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontWeight: 600, color, fontSize: 13, marginBottom: 4 }}>{title}</div>
      {note && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6, fontStyle: 'italic' }}>{note}</div>}
      <ul style={{ paddingLeft: 18, fontSize: 12, color: '#334155', maxHeight: 240, overflowY: 'auto' }}>
        {items.map((it, i) => <li key={i}>{render(it)}</li>)}
      </ul>
    </div>
  )
}

function DiffSummary({ diff }) {
  const s = diff.summary || {}
  return (
    <div style={{ padding: 10, background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 6, marginBottom: 12, fontSize: 13, color: '#0f172a' }}>
      <b>{s.unchanged || 0}</b> unchanged ·{' '}
      <b style={{ color: '#b45309' }}>{s.locationDiffs || 0}</b> location diffs ·{' '}
      <b style={{ color: '#b45309' }}>{s.ipDiffs || 0}</b> ip diffs ·{' '}
      <b style={{ color: '#b45309' }}>{s.cipDiffs || 0}</b> cip diffs ·{' '}
      <b style={{ color: '#1e40af' }}>{s.missingOnLocal || 0}</b> on source only ·{' '}
      <b style={{ color: '#64748b' }}>{s.extraOnLocal || 0}</b> on local only ·{' '}
      <b style={{ color: '#dc2626' }}>{s.blockedByNameCollision || 0}</b> blocked (name collision)
      <div style={{ marginTop: 4, fontSize: 11, color: '#475569' }}>Source: <b>{diff.sourceCourseCount}</b> IsApiOnly courses · Local: <b>{diff.localCourseCount}</b> IsApiOnly courses</div>
    </div>
  )
}

function DiffList({ diff }) {
  if (!diff.courses?.length) return null
  // Highlight courses with actual updates at the top
  const withChanges = diff.courses.filter(c => c.status === 'updates' || c.status === 'missing-on-local' || c.status === 'blocked-not-api-only')
  const unchanged   = diff.courses.filter(c => c.status === 'unchanged')
  const extras      = diff.courses.filter(c => c.status === 'extra-on-local')
  return (
    <div>
      {withChanges.length > 0 && (
        <ul style={{ marginTop: 0, paddingLeft: 18, fontSize: 12, color: '#0f172a', maxHeight: 360, overflowY: 'auto' }}>
          {withChanges.map((c, i) => <CourseDiffLine key={i} c={c} />)}
        </ul>
      )}
      {extras.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary style={{ fontSize: 12, color: '#64748b', cursor: 'pointer' }}>{extras.length} courses on this env not on source (not touched)</summary>
          <ul style={{ marginTop: 4, paddingLeft: 18, fontSize: 12, color: '#64748b' }}>
            {extras.map((c, i) => <li key={i}>#{c.courseId} {c.courseName}</li>)}
          </ul>
        </details>
      )}
      {unchanged.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary style={{ fontSize: 12, color: '#64748b', cursor: 'pointer' }}>{unchanged.length} unchanged</summary>
          <ul style={{ marginTop: 4, paddingLeft: 18, fontSize: 11, color: '#64748b' }}>
            {unchanged.map((c, i) => <li key={i}>{c.courseName}</li>)}
          </ul>
        </details>
      )}
    </div>
  )
}

function CourseDiffLine({ c }) {
  return (
    <li style={{ marginBottom: 8 }}>
      <b>{c.courseName}</b>{c.courseId ? <span style={{ color: '#64748b' }}> #{c.courseId}</span> : null}
      {' '}<span style={{
        fontSize: 10, fontWeight: 700, textTransform: 'uppercase', padding: '1px 6px', borderRadius: 4,
        background:
          c.status === 'missing-on-local' ? '#dbeafe' :
          c.status === 'blocked-not-api-only' ? '#fee2e2' : '#fef3c7',
        color:
          c.status === 'missing-on-local' ? '#1e40af' :
          c.status === 'blocked-not-api-only' ? '#b91c1c' : '#b45309',
      }}>{c.status}</span>
      {c.changes?.location && (
        <div style={{ marginLeft: 10, color: '#475569', fontSize: 11 }}>
          📍 lat/lng/city: ({c.changes.location.from.lat ?? '–'},{c.changes.location.from.lng ?? '–'},{c.changes.location.from.city ?? '–'}) → ({c.changes.location.to.lat ?? '–'},{c.changes.location.to.lng ?? '–'},{c.changes.location.to.city ?? '–'})
        </div>
      )}
      {c.changes?.ipUpserts?.length > 0 && (
        <div style={{ marginLeft: 10, color: '#475569', fontSize: 11 }}>
          🔌 ip upsert: {c.changes.ipUpserts.map(u => u.providerType).join(', ')}
        </div>
      )}
      {c.changes?.ipDeactivations?.length > 0 && (
        <div style={{ marginLeft: 10, color: '#b91c1c', fontSize: 11 }}>
          ⊘ ip deactivate: {c.changes.ipDeactivations.map(d => d.providerType).join(', ')}
        </div>
      )}
      {c.changes?.cipUpserts?.length > 0 && (
        <div style={{ marginLeft: 10, color: '#475569', fontSize: 11 }}>
          📦 cip upsert: products {c.changes.cipUpserts.map(u => u.productsId).join(', ')}
        </div>
      )}
      {c.changes?.reason && (
        <div style={{ marginLeft: 10, color: '#64748b', fontSize: 11 }}>{c.changes.reason}</div>
      )}
    </li>
  )
}

// ─── Seed provider classes + backfill ────────────────────────────────────

/**
 * v156 data state: ensure provider_classes has the two canonical rows
 * (InteGolf, Tee-Time.com) and every active golf_booking ip row's
 * ProviderClass_ID is set. Idempotent. Safety net for the autoSyncDb
 * silent-fail class of bug.
 */
function SeedProviderClassesCard() {
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')

  async function run() {
    setBusy(true); setError(''); setResult(null)
    try {
      const { data } = await migPost('/admin/data-migration/seed-provider-classes', {})
      setResult(data)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Seed failed')
    } finally { setBusy(false) }
  }

  return (
    <Card
      title="Seed provider classes (v156)"
      subtitle="Ensures the provider_classes table has InteGolf + Tee-Time.com and that every active golf_booking integration_providers row is classified. Idempotent. Click once after deploying v156 to make sure prod's data caught up even if autoSyncDb dropped a step."
    >
      <button onClick={run} disabled={busy} style={primaryBtn}>
        {busy ? 'Seeding…' : 'Seed + backfill provider classes'}
      </button>
      {error && <p style={errorStyle}>{error}</p>}
      {result && (
        <div style={{ marginTop: 16, padding: 12, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, fontSize: 13 }}>
          <div style={{ fontWeight: 600, color: '#15803d', marginBottom: 4 }}>
            ✓ Seeded {result.classesSeeded} classes · backfilled {result.apexBackfilled} apex + {result.integolfBackfilled} integolf rows
          </div>
          <div style={{ fontSize: 12, color: '#475569' }}>
            Final state: <b>{result.classedIntegolf}</b> InteGolf · <b>{result.classedApex}</b> Tee-Time.com · <b style={{ color: result.stillUnclassified > 0 ? '#b91c1c' : '#15803d' }}>{result.stillUnclassified}</b> unclassified
          </div>
        </div>
      )}
    </Card>
  )
}

// ─── Deduplicate integration_providers ───────────────────────────────────

/**
 * For each (Courses_ID, Category, ProviderType) combo with > 1 active
 * integration_providers row, keep the newest (highest ID_NO) and flip
 * IsActive=0 on the rest. Surfaces every group it touched so the operator
 * can spot-check. Built for prod where Fox Hollow #427 had 7 active
 * teetime-com rows, multiplying the Course Integrations list.
 */
function DedupeIntegrationProvidersCard() {
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')

  async function run() {
    setBusy(true); setError(''); setResult(null)
    try {
      const { data } = await migPost('/admin/data-migration/dedupe-integration-providers', {})
      setResult(data)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Dedupe failed')
    } finally { setBusy(false) }
  }

  return (
    <Card
      title="Deduplicate course integrations"
      subtitle="When the same course has multiple ACTIVE integration_providers rows for the same provider type, the /admin/course-integrations list multiplies the course's row. Keeps the newest row per (Courses_ID, Category, ProviderType) and deactivates the rest (no rows are deleted — recoverable). Idempotent."
    >
      <button onClick={run} disabled={busy} style={primaryBtn}>
        {busy ? 'Deduping…' : 'Deduplicate integration_providers'}
      </button>
      {error && <p style={errorStyle}>{error}</p>}
      {result && (
        <div style={{ marginTop: 16, padding: 12, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8 }}>
          <div style={{ fontWeight: 600, color: '#15803d', marginBottom: 6 }}>
            ✓ {result.groupsFound} duplicate group{result.groupsFound === 1 ? '' : 's'} found · {result.totalDeactivated} row{result.totalDeactivated === 1 ? '' : 's'} deactivated
          </div>
          {result.groups?.length > 0 && (
            <ul style={{ marginTop: 8, paddingLeft: 18, fontSize: 12, color: '#334155', maxHeight: 240, overflowY: 'auto' }}>
              {result.groups.map((g, i) => (
                <li key={i} style={{ marginBottom: 4 }}>
                  Course #{g.coursesId} · <code>{g.providerType}</code> — kept ip #{g.keptIpId}; deactivated ip #{g.deactivatedIpIds.join(', #')}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  )
}

// ─── Mark API-only courses ───────────────────────────────────────────────

/**
 * Flags every catalog-only / API-bookable course (Apex/TTC/TeeOn shells nobody
 * signed up to manage) with IsApiOnly=1 so the tournament platform excludes
 * them from course pickers and dashboards. Idempotent — clicking on a fresh
 * environment adds the column + index then backfills; clicking again on an
 * already-flagged DB is a no-op (only flips 0→1, never resets).
 */
function MarkApiOnlyCard() {
  const { t } = useLanguage()
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')

  async function run() {
    setBusy(true); setError(''); setResult(null)
    try {
      const { data } = await migPost('/admin/data-migration/mark-api-only', {})
      setResult(data)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || t('sp_mark_api_only_failed', 'Mark API-only failed'))
    } finally { setBusy(false) }
  }

  return (
    <Card
      title={t('sp_mark_api_only_title', 'Mark API-only courses')}
      subtitle={t('sp_mark_api_only_blurb', 'Flags every course in the catalog that has integration credentials wired (Apex, TTC, TeeOn) but no user attached. The tournament platform filters these out so its course pickers and dashboards only show courses someone actually signed up to manage. Idempotent — safe to click anytime.')}
    >
      <button onClick={run} disabled={busy} style={primaryBtn}>
        {busy ? t('sp_mark_api_only_busy', 'Marking…') : t('sp_mark_api_only_button', 'Mark API-only courses')}
      </button>
      {error && <p style={errorStyle}>{error}</p>}
      {result && (
        <div style={{ marginTop: 16, padding: 12, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8 }}>
          <div style={{ fontWeight: 600, color: '#15803d', marginBottom: 6 }}>
            {t('sp_mark_api_only_done_on', '✓ Done on')} {result.target?.db}
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#334155', lineHeight: 1.6 }}>
            {result.columnAdded && <li>{t('sp_mark_api_only_added_column', 'Added IsApiOnly column to courses.')}</li>}
            {result.indexAdded && <li>{t('sp_mark_api_only_added_index', 'Added idx_is_api_only index.')}</li>}
            <li><b>{result.affected}</b> {t('sp_mark_api_only_newly_flagged', 'course(s) newly flagged as API-only.')}</li>
            <li>
              {t('sp_mark_api_only_totals_label', 'Totals on this DB:')}{' '}
              <b>{result.totals?.apiOnly}</b> {t('sp_mark_api_only_totals_api', 'API-only')}{' · '}
              <b>{result.totals?.regular}</b> {t('sp_mark_api_only_totals_regular', 'regular (visible to tournament platform).')}
            </li>
          </ul>
        </div>
      )}
    </Card>
  )
}

// ─── Schema audit (parses dbVersions.js, surfaces ALL drift) ─────────────

/**
 * Comprehensive schema-drift detector. Reads tournament-api's dbVersions.js
 * to build the canonical "what should exist" schema, compares against the
 * live DB, and reports every missing column at once. Then lets the admin
 * auto-apply with one click.
 *
 * Unlike SchemaSyncCard (which only covers the hand-curated SCHEMA_ALTERS
 * hedge in PlayStay-API), this catches every ALTER ADD COLUMN ever migrated
 * — so it stays current automatically as new migrations land. Built for the
 * autoSyncDb silent-fail-on-migration bug. Per Todd, 2026-06-19.
 */
function SchemaAuditCard() {
  const [busy, setBusy] = useState(false)
  const [audit, setAudit] = useState(null)
  const [applyResult, setApplyResult] = useState(null)
  const [error, setError] = useState('')

  async function runAudit() {
    setBusy(true); setError(''); setAudit(null); setApplyResult(null)
    try {
      const { data } = await migGet('/db-sync/schema-audit')
      setAudit(data)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Schema audit failed')
    } finally { setBusy(false) }
  }

  async function applyFixes() {
    if (!audit || audit.totalMissingColumns === 0) return
    setBusy(true); setError(''); setApplyResult(null)
    try {
      const { data } = await migPost('/db-sync/schema-audit/apply', {})
      setApplyResult(data)
      // Re-run audit so the UI reflects post-apply state.
      const { data: post } = await migGet('/db-sync/schema-audit')
      setAudit(post)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Schema audit apply failed')
    } finally { setBusy(false) }
  }

  return (
    <Card
      title="Schema audit (comprehensive)"
      subtitle="Parses every CREATE TABLE and ALTER ADD COLUMN from dbVersions.js to figure out what the schema SHOULD look like, then compares to the live DB. Catches every missing column at once — including ones the 'Sync all schema' button (which uses a hand-curated list) doesn't know about. Built to work around autoSyncDb's silent-fail-on-migration bug."
    >
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={runAudit} disabled={busy} style={primaryBtn}>
          {busy ? 'Auditing…' : 'Run audit'}
        </button>
        {audit && audit.totalMissingColumns > 0 && (
          <button onClick={applyFixes} disabled={busy} style={{ ...primaryBtn, background: '#dc2626' }}>
            {busy ? 'Applying…' : `Apply ${audit.totalMissingColumns} fix${audit.totalMissingColumns === 1 ? '' : 'es'}`}
          </button>
        )}
      </div>
      {error && <p style={errorStyle}>{error}</p>}
      {audit && (
        <div style={{
          marginTop: 16, padding: 12, borderRadius: 8,
          background: audit.totalMissingColumns === 0 ? '#f0fdf4' : '#fef3c7',
          border: '1px solid ' + (audit.totalMissingColumns === 0 ? '#86efac' : '#fde68a'),
        }}>
          <div style={{ fontWeight: 600, color: audit.totalMissingColumns === 0 ? '#15803d' : '#b45309', marginBottom: 6 }}>
            {audit.totalMissingColumns === 0
              ? '✓ Schema is clean — all expected columns present'
              : `${audit.totalMissingColumns} missing column${audit.totalMissingColumns === 1 ? '' : 's'} across ${audit.tables.length} table${audit.tables.length === 1 ? '' : 's'}`}
          </div>
          <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>
            Audited {audit.totalExpectedTables} tables · {audit.totalMissingTables} missing entirely · {audit.totalMissingColumns} missing columns
          </div>
          {audit.tables.length > 0 && (
            <ul style={{ marginTop: 8, paddingLeft: 18, fontSize: 12, color: '#334155', maxHeight: 320, overflowY: 'auto' }}>
              {audit.tables.map((t, i) => (
                <li key={i} style={{ marginBottom: 6 }}>
                  <code>{t.table}</code>{t.tableMissing && <b style={{ color: '#b91c1c' }}> (table missing)</b>}
                  <ul style={{ paddingLeft: 16, marginTop: 4 }}>
                    {t.missing.map((m, j) => (
                      <li key={j}>
                        <code>{m.column}</code> <span style={{ color: '#64748b' }}>→ {m.ddl.length > 80 ? m.ddl.slice(0, 80) + '…' : m.ddl}</span>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {applyResult && (
        <div style={{ marginTop: 12, padding: 12, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8 }}>
          <div style={{ fontWeight: 600, color: '#15803d', marginBottom: 6 }}>
            ✓ Applied {applyResult.applied} · skipped {applyResult.skipped} · errored {applyResult.errored}
          </div>
          {applyResult.errored > 0 && (
            <ul style={{ marginTop: 8, paddingLeft: 18, fontSize: 12, color: '#b91c1c', maxHeight: 240, overflowY: 'auto' }}>
              {applyResult.results.filter(r => r.status === 'error').map((r, i) => (
                <li key={i}><code>{r.table}.{r.column}</code> — {r.error}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  )
}

// ─── Schema sync (always-available repair) ───────────────────────────────

function SchemaSyncCard() {
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')

  async function run() {
    setBusy(true); setError(''); setResult(null)
    try {
      const { data } = await migPost('/admin/data-migration/repair-all', {})
      setResult(data)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Schema sync failed')
    } finally { setBusy(false) }
  }

  return (
    <Card
      title="Sync schema"
      subtitle="Runs CREATE TABLE IF NOT EXISTS + ALTER ADD COLUMN for every known reseller-platform table. Idempotent — safe to click anytime. Use this if a page errors with 'Unknown column' or 'table doesn't exist'."
    >
      <button onClick={run} disabled={busy} style={primaryBtn}>
        {busy ? 'Syncing…' : 'Sync all schema'}
      </button>
      {error && <p style={errorStyle}>{error}</p>}
      {result && (
        <div style={{ marginTop: 16, padding: 12, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8 }}>
          <div style={{ fontWeight: 600, color: result.ok ? '#15803d' : '#b45309', marginBottom: 6 }}>
            {result.ok ? '✓ Schema sync complete' : 'Schema sync finished with errors'}
          </div>
          <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>
            Target: {result.target?.db} · {result.tablesSwept?.length || 0} tables swept
          </div>
          <ul style={{ marginTop: 8, paddingLeft: 18, fontSize: 12, color: '#334155', maxHeight: 320, overflowY: 'auto' }}>
            {result.results.map((r, i) => (
              <li key={i}>
                <code>{r.table}</code> → <b style={{ color: r.status === 'error' ? '#b91c1c' : r.status === 'created' ? '#15803d' : '#64748b' }}>{r.status}</b>
                {r.error && <span style={{ color: '#b91c1c' }}> — {r.error}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  )
}

// ─── Export ──────────────────────────────────────────────────────────────

function ExportCard() {
  const [scope, setScope] = useState('active')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function handleDownload() {
    setBusy(true); setError('')
    try {
      const { data } = await migGet(
        `/admin/data-migration/export?scope=${encodeURIComponent(scope)}`,
        { responseType: 'blob' }
      )
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
      const filename = `reseller-export-${scope}-${ts}.json`
      const url = URL.createObjectURL(data)
      const a = document.createElement('a')
      a.href = url; a.download = filename
      document.body.appendChild(a); a.click()
      document.body.removeChild(a); URL.revokeObjectURL(url)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Download failed')
    } finally { setBusy(false) }
  }

  return (
    <Card title="Download (Export)" subtitle="Snapshot this environment to a JSON file you can upload to another.">
      <label style={labelStyle}>
        Scope
        <select value={scope} onChange={e => setScope(e.target.value)} style={selectStyle}>
          <option value="active">Active accounts only (recommended)</option>
          <option value="all">Everything, including closed/test accounts</option>
          <option value="platform-configs">Platform configs only (Expedia + provider keys, no reseller data)</option>
        </select>
      </label>
      <div style={{ fontSize: 13, color: '#64748b', marginTop: 8 }}>
        {scope === 'active'
          ? 'Skips reseller accounts with Status="closed" and everything cascading off them.'
          : scope === 'platform-configs'
            ? 'Tiny export: just integration_providers (Expedia + golf provider keys) and platform_fee_settings. No reseller / course / user data. Use this to seed a fresh environment with the cross-environment plumbing rows only.'
            : 'Includes every reseller account regardless of status. Larger file.'}
      </div>
      <button onClick={handleDownload} disabled={busy} style={primaryBtn}>
        {busy ? 'Preparing…' : '↓ Download snapshot'}
      </button>
      {error && <p style={errorStyle}>{error}</p>}
    </Card>
  )
}

// ─── Import ──────────────────────────────────────────────────────────────

function ImportCard() {
  const [file, setFile] = useState(null)
  const [dryRun, setDryRun] = useState(true)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')
  const [repairBusy, setRepairBusy] = useState(false)
  const [repairResult, setRepairResult] = useState(null)

  // Tables flagged "missing on target" by the most recent dry-run.
  const missingTables = result?.summary?.perTable
    ?.filter(t => t.missing)
    ?.map(t => t.table) || []

  async function runRepair() {
    setRepairBusy(true); setRepairResult(null); setError('')
    try {
      const { data } = await migPost('/admin/data-migration/repair', { tables: missingTables })
      setRepairResult(data)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Repair failed')
    } finally { setRepairBusy(false) }
  }

  async function run() {
    if (!file) { setError('Pick a snapshot file first.'); return }
    setBusy(true); setError(''); setResult(null)
    try {
      const text = await file.text()
      let body
      try { body = JSON.parse(text) }
      catch { throw new Error('Selected file is not valid JSON.') }
      const { data } = await migPost(
        `/admin/data-migration/import${dryRun ? '?dryRun=1' : ''}`,
        body
      )
      setResult(data)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Import failed')
    } finally { setBusy(false) }
  }

  return (
    <Card title="Upload (Import)" subtitle="Apply a snapshot file from another environment to this one.">
      <label style={labelStyle}>
        Snapshot file
        <input
          type="file"
          accept="application/json,.json"
          onChange={e => { setFile(e.target.files?.[0] || null); setResult(null); setError('') }}
          style={{ marginTop: 6 }}
        />
      </label>

      <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} />
        <span>Dry run (report what would change, no inserts)</span>
      </label>

      <button onClick={run} disabled={!file || busy} style={primaryBtn}>
        {busy ? 'Running…' : (dryRun ? 'Preview import' : '↑ Run import')}
      </button>

      {error && <p style={errorStyle}>{error}</p>}
      {result && <ImportResult result={result} />}

      {missingTables.length > 0 && (
        <div style={{ marginTop: 16, padding: 12, background: '#fef3c7', border: '1px solid #fde047', borderRadius: 8 }}>
          <div style={{ fontWeight: 600, color: '#92400e', marginBottom: 4 }}>
            {missingTables.length} table(s) missing on the target DB:
          </div>
          <code style={{ fontSize: 12, color: '#7c2d12' }}>{missingTables.join(', ')}</code>
          <div style={{ fontSize: 12, color: '#7c2d12', margin: '8px 0' }}>
            Click below to create them on the target via <code>CREATE TABLE IF NOT EXISTS</code>, then re-run the import.
          </div>
          <button onClick={runRepair} disabled={repairBusy} style={{ ...primaryBtn, marginTop: 4, background: '#b45309' }}>
            {repairBusy ? 'Creating tables…' : `Repair ${missingTables.length} missing table(s)`}
          </button>
          {repairResult && (
            <ul style={{ marginTop: 12, paddingLeft: 18, fontSize: 12, color: '#7c2d12' }}>
              {repairResult.results.map((r, i) => (
                <li key={i}>
                  <code>{r.table}</code> → <b>{r.status}</b>
                  {r.error && <span style={{ color: '#b91c1c' }}> — {r.error}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  )
}

function ImportResult({ result }) {
  const { summary, source, target, dryRun, ok } = result
  return (
    <div style={{ marginTop: 16, padding: 16, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <strong style={{ color: ok ? '#15803d' : '#b91c1c' }}>
          {dryRun ? 'Dry-run complete' : (ok ? 'Import complete' : 'Import finished with errors')}
        </strong>
        <span style={{ color: '#64748b', fontSize: 12 }}>
          {source.from} → {target.db}
        </span>
      </div>
      {summary.schemaMismatch && (
        <div style={{ marginBottom: 8, padding: 8, background: '#fef3c7', border: '1px solid #fde047', borderRadius: 6, fontSize: 13 }}>
          Schema mismatch: source v{summary.schemaMismatch.source}, target v{summary.schemaMismatch.target}. Inserts will still try, skipping columns the target doesn't have.
        </div>
      )}
      <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ textAlign: 'left', color: '#64748b' }}>
            <th style={th}>Table</th>
            <th style={thNum}>In file</th>
            <th style={thNum}>Inserted</th>
            <th style={thNum}>Existed</th>
            <th style={thNum}>Errors</th>
          </tr>
        </thead>
        <tbody>
          {summary.perTable.map(t => (
            <tr key={t.table} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={td}>{t.table}{t.missing && <span style={{ color: '#dc2626', marginLeft: 6 }}>(table missing on target)</span>}</td>
              <td style={tdNum}>{t.inExport}</td>
              <td style={{ ...tdNum, color: t.inserted ? '#15803d' : '#94a3b8' }}>{t.inserted}</td>
              <td style={{ ...tdNum, color: '#64748b' }}>{t.skipped}</td>
              <td style={{ ...tdNum, color: t.errors ? '#b91c1c' : '#94a3b8' }}>{t.errors}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #94a3b8', fontWeight: 600 }}>
            <td style={td}>Total</td>
            <td style={tdNum}>{summary.totals.inExport}</td>
            <td style={tdNum}>{summary.totals.inserted}</td>
            <td style={tdNum}>{summary.totals.skipped}</td>
            <td style={{ ...tdNum, color: summary.totals.errors ? '#b91c1c' : '#0f172a' }}>{summary.totals.errors}</td>
          </tr>
        </tfoot>
      </table>
      {summary.errors?.length > 0 && (
        <details style={{ marginTop: 12 }}>
          <summary style={{ cursor: 'pointer', color: '#b91c1c', fontSize: 13 }}>{summary.errors.length} error(s) — click to expand</summary>
          <ul style={{ marginTop: 8, fontSize: 12, color: '#7f1d1d', maxHeight: 240, overflowY: 'auto' }}>
            {summary.errors.map((e, i) => (
              <li key={i}><code>{e.table}</code> id={e.id ?? '?'}: <code>{e.code}</code> {e.message}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}

// ─── Layout primitives ──────────────────────────────────────────────────

/**
 * EmailTestCard — send a real test message through the same path production
 * uses (password resets, invites, booking confirmations). Confirms this
 * environment's SMTP credentials are actually saved + working, instead of
 * inferring it from a password reset that hides failures behind a generic
 * "if an account exists…" message.
 */
function EmailTestCard() {
  const [to, setTo] = useState('')
  const [busy, setBusy] = useState(false)
  const [res, setRes] = useState(null)
  const [error, setError] = useState('')

  async function run() {
    setBusy(true); setError(''); setRes(null)
    try {
      const { data } = await tournamentApi.post('/admin/data-migration/email-test', { to: to.trim() })
      setRes(data)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Test failed')
    } finally { setBusy(false) }
  }

  return (
    <Card
      title="Email / SMTP test"
      subtitle="Sends a real test message through the same sender production uses (password resets, invites, booking confirmations). Confirms this environment's SMTP credentials are saved and working. Run it on whichever environment you're checking."
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13, color: '#334155', display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 280px' }}>
          Send test to
          <input value={to} onChange={e => setTo(e.target.value)} placeholder="you@example.com" style={{ ...testInp }} />
        </label>
        <button onClick={run} disabled={busy || !to.trim()} style={{ ...primaryBtn, marginTop: 0, opacity: (busy || !to.trim()) ? 0.55 : 1 }}>
          {busy ? 'Sending…' : 'Send test email'}
        </button>
      </div>
      {error && <p style={errorStyle}>{error}</p>}

      {res && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: res.ok ? '#15803d' : '#b91c1c', marginBottom: 8 }}>
            {res.ok ? '✓ ' : '✗ '}{res.message}
          </div>
          {res.smtp && (
            <div style={{ fontSize: 12, color: '#334155', marginBottom: 6 }}>
              SMTP: <code>{res.smtp.user}</code> @ <code>{res.smtp.host}:{res.smtp.port}</code> · from <code>{res.smtp.from}</code> · password {res.smtp.passwordSet ? 'set ✓' : <strong style={{ color: '#b91c1c' }}>MISSING ✗</strong>}
            </div>
          )}
          {!res.ok && res.stage === 'config' && res.hint && (
            <div style={{ fontSize: 12, color: '#b45309', marginBottom: 6 }}>{res.hint}</div>
          )}
          {res.intercepted && (
            <div style={{ fontSize: 12, color: '#b45309', marginBottom: 6 }}>⚠ Test-mode email intercept is ON — the message was redirected to the internal test inbox, not the recipient above.</div>
          )}
          {res.ok && (
            <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.9 }}>
              accepted: <code>{(res.accepted || []).join(', ') || '—'}</code> · rejected: <code style={{ color: res.rejected?.length ? '#b91c1c' : undefined }}>{(res.rejected || []).join(', ') || '—'}</code><br />
              SMTP response: <code>{res.response || '—'}</code><br />
              message-id: <code style={{ fontSize: 11 }}>{res.messageId || '—'}</code>
            </div>
          )}
          {res.ok && !res.intercepted && (res.rejected?.length ? (
            <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 8 }}>⚠ SMTP <strong>rejected</strong> this address — it's invalid or blocked at submit time.</div>
          ) : (
            <div style={{ fontSize: 12, color: '#b45309', marginTop: 8 }}>
              Accepted by SMTP (250). <strong>If the recipient still doesn't receive it</strong>, the address is most likely on the AWS SES <strong>suppression list</strong> (a prior bounce/complaint — SES accepts then silently drops), or it's being spam-filtered on their side. Search the message-id above in the SES console, or check SES → Suppression list for this address.
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

/**
 * TeeTimeCourseProbeCard — "is this Tee-Time listing actually two courses?"
 * Runs a read-only probe from THIS server (run it on production — tee-time.com
 * IP-allowlists the prod server). Reports the distinct course names in the
 * mapped org's live tee times + nearby orgs from a radius search, and dumps the
 * full JSON to copy-paste back.
 */
// Explains how the golf markup resolves for a reseller + course (why a % is
// applied). Read-only.
function MarkupDebugCard() {
  const [resellers, setResellers] = useState([])
  const [resellerId, setResellerId] = useState('')
  const [courseQuery, setCourseQuery] = useState('')
  const [courseResults, setCourseResults] = useState([])
  const [selectedCourse, setSelectedCourse] = useState(null) // { id, name }
  const [rawPrice, setRawPrice] = useState('300')
  const [busy, setBusy] = useState(false)
  const [data, setData] = useState(null)
  const [error, setError] = useState('')

  const labelCol = { fontSize: 13, color: '#334155', display: 'flex', flexDirection: 'column', gap: 4 }

  // Load the reseller list once for the dropdown.
  useEffect(() => {
    tournamentApi.get('/admin/data-migration/diagnostics-lookup', { params: { type: 'resellers' } })
      .then(r => setResellers(r.data?.resellers || [])).catch(() => {})
  }, [])

  // Debounced course typeahead (paused once a course is picked).
  useEffect(() => {
    if (selectedCourse) return
    const q = courseQuery.trim()
    if (q.length < 2) { setCourseResults([]); return }
    const t = setTimeout(() => {
      tournamentApi.get('/admin/data-migration/diagnostics-lookup', { params: { type: 'courses', q } })
        .then(r => setCourseResults(r.data?.courses || [])).catch(() => {})
    }, 250)
    return () => clearTimeout(t)
  }, [courseQuery, selectedCourse])

  async function run() {
    setBusy(true); setError(''); setData(null)
    try {
      const params = { resellerAccountId: resellerId }
      if (selectedCourse) params.coursesId = selectedCourse.id
      if (rawPrice.trim()) params.rawPrice = rawPrice.trim()
      const { data } = await tournamentApi.get('/admin/data-migration/markup-debug', { params })
      setData(data)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Markup debug failed')
    } finally { setBusy(false) }
  }

  const gm = data?.golfMarkup
  return (
    <Card
      title="Golf markup debug (why is this % being applied?)"
      subtitle="Read-only. Pick a reseller + course; shows the reseller's per-provider-class golf-markup overrides, the course's resolved provider class, any managed-parent markup, and the FINAL % the pricing engine applies — with a worked example."
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={labelCol}>Reseller
          <select value={resellerId} onChange={e => setResellerId(e.target.value)} style={{ ...testInp, minWidth: 230 }}>
            <option value="">— Select a reseller —</option>
            {resellers.map(r => <option key={r.id} value={r.id}>{r.name} (#{r.id})</option>)}
          </select>
        </label>
        <label style={{ ...labelCol, flex: '1 1 260px', position: 'relative' }}>Course
          <input
            value={selectedCourse ? selectedCourse.name : courseQuery}
            onChange={e => { setSelectedCourse(null); setCourseQuery(e.target.value) }}
            placeholder="type a course name…" style={testInp}
          />
          {!selectedCourse && courseResults.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, marginTop: 2, maxHeight: 260, overflow: 'auto', boxShadow: '0 6px 20px rgba(15,23,42,0.12)' }}>
              {courseResults.map(c => (
                <div key={c.id} onClick={() => { setSelectedCourse({ id: c.id, name: c.name }); setCourseResults([]) }}
                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f1f5f9' }}>
                  {c.name} <span style={{ color: '#94a3b8', fontSize: 11 }}>#{c.id} · {c.provider || 'no provider'}</span>
                </div>
              ))}
            </div>
          )}
        </label>
        <label style={labelCol}>Sample raw price
          <input value={rawPrice} onChange={e => setRawPrice(e.target.value.replace(/[^\d.]/g, ''))} inputMode="decimal" placeholder="300" style={{ ...testInp, width: 110 }} />
        </label>
        <button onClick={run} disabled={busy || !resellerId} style={{ ...primaryBtn, marginTop: 0, opacity: (busy || !resellerId) ? 0.55 : 1 }}>
          {busy ? 'Running…' : 'Run markup debug'}
        </button>
      </div>
      {error && <p style={errorStyle}>{error}</p>}

      {data && (
        <div style={{ marginTop: 16, fontSize: 13, color: '#0f172a' }}>
          <div><strong>Reseller:</strong> {data.reseller.name} (#{data.reseller.id}){data.reseller.parentResellerId ? ` · managed child of #${data.reseller.parentResellerId}` : ' · top-level'}{data.reseller.parentGolfMarkupPercent ? ` · parent golf markup ${data.reseller.parentGolfMarkupPercent}%` : ''}</div>
          {data.course
            ? <div style={{ marginTop: 4 }}><strong>Course:</strong> {data.course.name} (#{data.course.id}) · integration {data.course.integrationProviderType || '(none)'} · <strong style={{ color: data.course.providerClassSlug ? '#0f172a' : '#b91c1c' }}>provider class: {data.course.providerClassName || data.course.providerClassSlug || 'NONE'}</strong></div>
            : <div style={{ marginTop: 4, color: '#b45309' }}>No course matched — reseller overrides only.</div>}

          <div style={{ marginTop: 12, fontWeight: 600 }}>Golf markup overrides (this reseller)</div>
          <table style={{ borderCollapse: 'collapse', marginTop: 4 }}>
            <tbody>
              <tr style={{ color: '#64748b' }}><td style={mdCell}>provider class</td><td style={mdCell}>value</td></tr>
              {gm.overrides.length === 0 && <tr><td style={mdCell} colSpan={2}>(none — uses platform default {gm.platformDefault})</td></tr>}
              {gm.overrides.map((o, i) => <tr key={i}><td style={mdCell}>{o.className || o.classSlug}</td><td style={mdCell}>{o.value}</td></tr>)}
            </tbody>
          </table>

          <div style={{ marginTop: 12, padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
            <div>Resolved from: <strong>{gm.resolvedFrom}</strong></div>
            <div>Final markup applied: <strong style={{ fontSize: 15 }}>{gm.finalMarkup}</strong></div>
            <div style={{ marginTop: 4 }}>Example: raw <strong>${gm.example.rawPrice}</strong> → <strong>${gm.example.markedUpPrice}</strong></div>
            {gm.notes.map((n, i) => <div key={i} style={{ marginTop: 6, color: '#b45309', fontSize: 12.5 }}>⚠ {n}</div>)}
          </div>
        </div>
      )}
    </Card>
  )
}
const mdCell = { border: '1px solid #e2e8f0', padding: '4px 12px', textAlign: 'left' }

// Reconciles our courses against the Tee-Time.com catalog and flags the exact
// wiring each course is missing. Read-only; run on production.
function TtcCourseDiagnosticsCard() {
  const [mode, setMode] = useState('ref')
  const [query, setQuery] = useState('218')
  const [live, setLive] = useState(false)
  const [busy, setBusy] = useState(false)
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [showMissing, setShowMissing] = useState(false)

  const labelCol = { fontSize: 13, color: '#334155', display: 'flex', flexDirection: 'column', gap: 4 }
  const linkBtn = { background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 12, marginLeft: 8, padding: 0, textDecoration: 'underline' }
  const flagColor = (f) => f.startsWith('NO_CACHE_ROWS') ? '#64748b' : /NOT_IN_OUR_DB|NOT_FOUND_IN_PROVIDER/.test(f) ? '#b91c1c' : '#b45309'

  async function run() {
    setBusy(true); setError(''); setData(null); setShowMissing(false)
    try {
      const params = { mode }
      if (live) params.live = 1
      if (mode === 'ref') params.refs = query
      else if (mode === 'id') params.ids = query
      else if (mode === 'course') params.name = query
      const { data } = await tournamentApi.get('/admin/data-migration/ttc-course-diagnostics', { params })
      setData(data)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Diagnostics failed')
    } finally { setBusy(false) }
  }

  const recon = data?.reconciliation
  return (
    <Card
      title="Tee-Time.com course diagnostics (missing / mis-wired courses)"
      subtitle="Run on PRODUCTION — tee-time.com allowlists the prod IP. Read-only: reconciles our courses against the Tee-Time.com catalog and flags exactly what each course is missing (not imported, inactive, no per-course integration row, no reseller link, bad coords). Add the live probe to confirm real availability."
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={labelCol}>Mode
          <select value={mode} onChange={e => setMode(e.target.value)} style={{ ...testInp, width: 210 }}>
            <option value="all">All courses (reconcile)</option>
            <option value="ref">By Tee-Time.com id</option>
            <option value="id">By our course id</option>
            <option value="course">By course name</option>
          </select>
        </label>
        {mode !== 'all' && (
          <label style={{ ...labelCol, flex: '1 1 240px' }}>
            {mode === 'ref' ? 'Tee-Time org id(s), comma-sep' : mode === 'id' ? 'Our course ID(s), comma-sep' : 'Name contains'}
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder={mode === 'course' ? 'bay of quinte' : '218, 15'} style={testInp} />
          </label>
        )}
        <label style={{ fontSize: 13, color: '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={live} onChange={e => setLive(e.target.checked)} /> Live availability probe
        </label>
        <button onClick={run} disabled={busy} style={{ ...primaryBtn, marginTop: 0, opacity: busy ? 0.55 : 1 }}>
          {busy ? 'Running…' : 'Run diagnostics'}
        </button>
      </div>
      {error && <p style={errorStyle}>{error}</p>}

      {data && (
        <div style={{ marginTop: 16 }}>
          {!data.providerKey && <p style={errorStyle}>No Tee-Time.com API key found on this server.</p>}
          {data.catalogError && <p style={errorStyle}>Provider catalog fetch failed: {data.catalogError} (run on production — tee-time.com allowlists the prod IP).</p>}
          {recon && (
            <div style={{ fontSize: 13, color: '#0f172a', marginBottom: 12 }}>
              Catalog <strong>{recon.catalog}</strong> · we have <strong>{recon.weHave}</strong> · <strong style={{ color: recon.missingCount ? '#b45309' : '#15803d' }}>missing {recon.missingCount}</strong>{recon.danglingCount ? ` · dangling ${recon.danglingCount}` : ''}
              {recon.missingCount > 0 && <button onClick={() => setShowMissing(s => !s)} style={linkBtn}>{showMissing ? 'hide' : 'show'} missing</button>}
              {showMissing && (
                <div style={{ marginTop: 8, maxHeight: 220, overflow: 'auto', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: 10, fontFamily: 'monospace', fontSize: 12 }}>
                  {recon.missing.map(m => <div key={m.id}>#{m.id} {m.name}</div>)}
                </div>
              )}
            </div>
          )}
          {(data.results || []).map((r, i) => (
            <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginBottom: 10 }}>
              <div style={{ fontWeight: 600, color: '#0f172a' }}>{r.ours?.name || r.provider?.name || '(unknown)'}</div>
              <div style={{ fontSize: 12.5, color: '#475569', marginTop: 4 }}>
                {r.ours
                  ? `ours: #${r.ours.id} · ref ${r.ours.externalRef} · active ${r.ours.isResellerActive} · integ ${r.ours.hasActiveIntegration} · resellerLinks ${r.ours.resellerLinks} · coords ${r.ours.lat},${r.ours.lng} · ${r.ours.province} · cache ${r.ours.cache.total}`
                  : 'ours: NOT IN DB'}
              </div>
              <div style={{ fontSize: 12.5, color: '#475569' }}>
                {r.provider ? `provider: org ${r.provider.id} · ${r.provider.city}/${r.provider.province} · coords ${r.provider.lat},${r.provider.lng} · window ${r.provider.bookingWindowDays}d` : 'provider: not in catalog'}
              </div>
              {r.live && <div style={{ fontSize: 12.5, color: '#0f766e' }}>live: {JSON.stringify(r.live)}</div>}
              <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {r.flags.map((f, j) => <span key={j} style={{ fontSize: 11, fontWeight: 600, background: '#fff7ed', color: flagColor(f), border: `1px solid ${flagColor(f)}33`, borderRadius: 999, padding: '2px 8px' }}>{f}</span>)}
                {!r.flags.length && <span style={{ fontSize: 12, color: '#15803d' }}>✓ no issues</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

function TeeTimeCourseProbeCard() {
  const [courseId, setCourseId] = useState('290')
  const [date, setDate] = useState('')
  const [probeIds, setProbeIds] = useState('20, 27, 50, 69, 76, 79, 87, 88, 94, 99, 103')
  const [busy, setBusy] = useState(false)
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)

  async function run() {
    setBusy(true); setError(''); setData(null); setCopied(false)
    try {
      const { data } = await tournamentApi.get('/admin/data-migration/teetime-course-probe', {
        params: { courseId: Number(courseId) || 290, date: date || undefined, probeIds: probeIds || undefined },
      })
      setData(data)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Probe failed')
    } finally { setBusy(false) }
  }

  const pretty = data ? JSON.stringify(data, null, 2) : ''
  async function copy() {
    try { await navigator.clipboard.writeText(pretty); setCopied(true); setTimeout(() => setCopied(false), 2000) } catch {}
  }

  const mapped = data?.mappedOrg
  const mappedCourses = mapped?.distinctCourses || mapped?.courses || []
  const twoCourses = Array.isArray(mappedCourses) && mappedCourses.length > 1

  return (
    <Card
      title="Tee-Time course probe (is a listing actually two 18s?)"
      subtitle="Run this on PRODUCTION — tee-time.com allowlists the prod server's IP, so it 403s from a laptop. It lists the distinct course names in the mapped org's live tee times and the nearby orgs from a radius search, then dumps the full JSON to copy back."
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13, color: '#334155', display: 'flex', flexDirection: 'column', gap: 4 }}>
          Course ID
          <input value={courseId} onChange={e => setCourseId(e.target.value.replace(/\D/g, ''))} inputMode="numeric" placeholder="290" style={{ ...testInp, width: 100 }} />
        </label>
        <label style={{ fontSize: 13, color: '#334155', display: 'flex', flexDirection: 'column', gap: 4 }}>
          Date (optional, YYYY-MM-DD)
          <input value={date} onChange={e => setDate(e.target.value)} placeholder="(defaults to +30 days)" style={{ ...testInp, width: 200 }} />
        </label>
        <label style={{ fontSize: 13, color: '#334155', display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 240px' }}>
          Extra org ids to inspect (skipped in the import)
          <input value={probeIds} onChange={e => setProbeIds(e.target.value)} placeholder="20, 27, 50…" style={{ ...testInp }} />
        </label>
        <button onClick={run} disabled={busy} style={{ ...primaryBtn, marginTop: 0, opacity: busy ? 0.55 : 1 }}>
          {busy ? 'Probing…' : 'Run probe'}
        </button>
      </div>
      {error && <p style={errorStyle}>{error}</p>}

      {data && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, color: '#0f172a', marginBottom: 8 }}>
            <strong>{data.course?.name}</strong> (#{data.course?.id}) · org <code>{data.orgId}</code>
            {mapped?.status && mapped.status !== 200
              ? <span style={{ marginLeft: 8, color: '#b91c1c' }}>· org fetch HTTP {mapped.status} {mapped.error ? `(${mapped.error})` : ''}</span>
              : <span style={{ marginLeft: 8 }}>· {mapped?.datesWithInventory ?? 0}/{mapped?.datesChecked ?? 0} dates had inventory · <strong style={{ color: mappedCourses.length === 0 ? '#b91c1c' : twoCourses ? '#b45309' : '#15803d' }}>{mappedCourses.length} distinct course name(s)</strong></span>}
          </div>
          {mappedCourses.length > 0 && (
            <div style={{ fontSize: 12, color: '#334155', marginBottom: 8 }}>
              Course names in org {data.orgId}: {mappedCourses.map((c, i) => <span key={i}>{i > 0 && ' · '}<strong>{c.course}</strong> ({c.holes ?? '?'}h, {c.count})</span>)}
            </div>
          )}
          {mappedCourses.length === 0 && mapped?.status === 200 && (
            <div style={{ fontSize: 12, color: '#b45309', marginBottom: 8 }}>
              ⚠ Org {data.orgId} returned no tee times across {mapped?.datesChecked} dates — no bookable inventory via Tee-Time right now.
            </div>
          )}
          {Array.isArray(data.radiusSearch?.orgs) && (
            <div style={{ fontSize: 12, color: '#334155', marginBottom: 8 }}>
              Nearby orgs: {data.radiusSearch.orgs.map((o, i) => <span key={i}>{i > 0 && ' · '}#{o.id} {o.name} ({o.city})</span>)}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, marginBottom: 4 }}>
            <button onClick={copy} style={{ padding: '7px 14px', fontSize: 13, fontWeight: 600, background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
              {copied ? '✓ Copied' : 'Copy JSON'}
            </button>
            <span style={{ fontSize: 12, color: '#64748b' }}>Paste this whole block back to Claude.</span>
          </div>
          <textarea readOnly value={pretty} onFocus={e => e.target.select()}
            style={{ width: '100%', minHeight: 280, fontFamily: 'monospace', fontSize: 11, padding: 10, border: '1px solid #e2e8f0', borderRadius: 6, background: '#f8fafc', color: '#0f172a', resize: 'vertical' }} />
        </div>
      )}
    </Card>
  )
}

function Card({ title, subtitle, children }) {
  return (
    <div style={{
      flex: '1 1 480px', background: '#fff', border: '1px solid #e2e8f0',
      borderRadius: 10, padding: 24, boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    }}>
      <h2 style={{ margin: 0, fontSize: 18, color: '#0f172a' }}>{title}</h2>
      {subtitle && <p style={{ marginTop: 4, marginBottom: 16, color: '#64748b', fontSize: 13 }}>{subtitle}</p>}
      {children}
    </div>
  )
}

// ─── Import InteGolf courses (JSON catalog drop) ─────────────────────────

/**
 * IntegolfCoursesImportCard — upload a JSON file from the InteGolf MongoDB
 * `courses` collection (e.g. integolf-production.courses.json) and re-import
 * those rows into whichever environment the user is on. Lets us seed prod
 * with TeeOn/Apex/TTC catalog rows + their credentials without a script run.
 *
 * Flow:
 *   1. Pick file → parse JSON client-side
 *   2. Click "Preview" → POST { entries, dryRun: true } → render plan table
 *   3. Tick rows to import (NEW pre-checked, EXISTS unchecked)
 *   4. Click "Import selected" → POST { entries, dryRun: false, selections }
 *      → server runs the planner again + commits the chosen idx in a tx
 *   5. Result panel shows created/wired counts.
 *
 * Idempotent — re-uploading the same JSON after a commit shows the previously
 * inserted courses as EXISTS (matched by name) and they're not duplicated.
 */
function IntegolfCoursesImportCard() {
  const { t } = useLanguage()
  const [file, setFile] = useState(null)
  const [entries, setEntries] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [plan, setPlan] = useState(null)        // { plan: [...], summary, target }
  const [selected, setSelected] = useState(new Set())
  const [result, setResult] = useState(null)    // commit result

  function reset() {
    setEntries(null); setPlan(null); setSelected(new Set()); setResult(null); setError('')
  }

  async function handleFile(f) {
    reset(); setFile(f || null)
    if (!f) return
    try {
      const txt = await f.text()
      const parsed = JSON.parse(txt)
      if (!Array.isArray(parsed)) throw new Error(t('sp_integolf_import_not_array', 'Expected a JSON array of course entries.'))
      setEntries(parsed)
    } catch (err) {
      setError(err.message || t('sp_integolf_import_parse_failed', 'Could not parse the JSON file.'))
    }
  }

  async function preview() {
    if (!entries) return
    setBusy(true); setError(''); setPlan(null); setResult(null)
    try {
      const { data } = await migPost('/admin/data-migration/import-integolf-courses', {
        entries, dryRun: true,
      })
      setPlan(data)
      // Default selection: every 'create-and-wire' row.
      const next = new Set()
      for (const p of (data.plan || [])) {
        if (p.action === 'create-and-wire') next.add(p.idx)
      }
      setSelected(next)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || t('sp_integolf_import_preview_failed', 'Preview failed'))
    } finally { setBusy(false) }
  }

  async function commit() {
    if (!entries || !plan || selected.size === 0) return
    setBusy(true); setError(''); setResult(null)
    try {
      const { data } = await migPost('/admin/data-migration/import-integolf-courses', {
        entries, dryRun: false, selections: Array.from(selected),
      })
      setResult(data)
      // Refresh the plan so EXISTS rows reflect the new state.
      setPlan({ plan: data.plan, summary: data.summary, target: data.target })
      setSelected(new Set())
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || t('sp_integolf_import_commit_failed', 'Import failed'))
    } finally { setBusy(false) }
  }

  function toggle(idx) {
    const next = new Set(selected)
    if (next.has(idx)) next.delete(idx); else next.add(idx)
    setSelected(next)
  }
  function toggleAll(action) {
    const next = new Set(selected)
    const matching = (plan?.plan || []).filter(p => p.action === action).map(p => p.idx)
    const allOn = matching.every(i => next.has(i))
    for (const i of matching) {
      if (allOn) next.delete(i); else next.add(i)
    }
    setSelected(next)
  }

  return (
    <Card
      title={t('sp_integolf_import_title', 'Import InteGolf courses JSON')}
      subtitle={t('sp_integolf_import_blurb', 'Upload a course catalog dump (e.g. integolf-production.courses.json) to seed this environment with TeeOn/Apex/TTC courses and their booking credentials. Preview the plan first; nothing is written until you click Import. NEW rows are pre-selected; EXISTS rows (matched by name) are off by default so existing credentials are never overwritten by accident.')}
    >
      <label style={labelStyle}>
        {t('sp_integolf_import_file_label', 'JSON file')}
        <input
          type="file"
          accept="application/json,.json"
          onChange={e => handleFile(e.target.files?.[0] || null)}
          style={{ marginTop: 6 }}
        />
      </label>

      <button
        onClick={preview}
        disabled={!entries || busy}
        style={primaryBtn}
      >
        {busy && !plan ? t('sp_integolf_import_previewing', 'Previewing…') : t('sp_integolf_import_preview_btn', 'Preview import')}
      </button>

      {error && <p style={errorStyle}>{error}</p>}

      {plan && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, color: '#475569', marginBottom: 8 }}>
            {t('sp_integolf_import_target_lbl', 'Target:')} <code>{plan.target?.db}</code> · {plan.summary?.total || 0} {t('sp_integolf_import_entries_word', 'entries')}{' '}
            ({plan.summary?.newCount || 0} {t('sp_integolf_import_new_word', 'new')},{' '}
            {plan.summary?.wireCredsCount || 0} {t('sp_integolf_import_exists_word', 'exists')},{' '}
            {plan.summary?.skipCount || 0} {t('sp_integolf_import_skip_word', 'skip')})
          </div>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead style={{ background: '#f8fafc', textAlign: 'left', color: '#64748b' }}>
                <tr>
                  <th style={{ padding: '6px 8px', width: 30 }}></th>
                  <th style={{ padding: '6px 8px' }}>{t('sp_integolf_import_col_action', 'Action')}</th>
                  <th style={{ padding: '6px 8px' }}>{t('sp_integolf_import_col_code', 'Code')}</th>
                  <th style={{ padding: '6px 8px' }}>{t('sp_integolf_import_col_course', 'Course')}</th>
                  <th style={{ padding: '6px 8px' }}>{t('sp_integolf_import_col_adapter', 'Adapter')}</th>
                  <th style={{ padding: '6px 8px' }}>{t('sp_integolf_import_col_notes', 'Notes')}</th>
                </tr>
              </thead>
              <tbody>
                {plan.plan.map(p => (
                  <tr key={p.idx} style={{
                    borderTop: '1px solid #f1f5f9',
                    background: p.action === 'skip' ? '#fef2f2'
                      : selected.has(p.idx) ? '#f0fdf4'
                      : 'transparent',
                  }}>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                      {p.action !== 'skip' && (
                        <input
                          type="checkbox"
                          checked={selected.has(p.idx)}
                          onChange={() => toggle(p.idx)}
                          disabled={busy}
                        />
                      )}
                    </td>
                    <td style={{ padding: '6px 8px', fontWeight: 600,
                      color: p.action === 'create-and-wire' ? '#15803d'
                        : p.action === 'wire-creds' ? '#0e7490'
                        : '#b91c1c' }}>
                      {p.action === 'create-and-wire' ? t('sp_integolf_import_act_new', 'NEW')
                        : p.action === 'wire-creds' ? t('sp_integolf_import_act_wire', 'WIRE')
                        : t('sp_integolf_import_act_skip', 'SKIP')}
                    </td>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 12 }}>{p.courseCode}</td>
                    <td style={{ padding: '6px 8px' }}>
                      {p.courseName}
                      {p.courseId && <span style={{ color: '#94a3b8', marginLeft: 6 }}>→ #{p.courseId}</span>}
                    </td>
                    <td style={{ padding: '6px 8px', fontSize: 12, color: '#64748b' }}>{p.adapter || '—'}</td>
                    <td style={{ padding: '6px 8px', fontSize: 12, color: '#64748b' }}>
                      {p.reason && <em>{p.reason}</em>}
                      {p.alreadyHasCredentials && (
                        <span style={{ color: '#92400e' }}>
                          {t('sp_integolf_import_already_wired', 'already has credentials — overwrites if selected')}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 12 }}>
            <button onClick={() => toggleAll('create-and-wire')} disabled={busy} style={smallToggleBtn}>
              {t('sp_integolf_import_toggle_new', 'Toggle all NEW')}
            </button>
            <button onClick={() => toggleAll('wire-creds')} disabled={busy} style={smallToggleBtn}>
              {t('sp_integolf_import_toggle_exists', 'Toggle all EXISTS')}
            </button>
            <span style={{ fontSize: 12, color: '#64748b' }}>
              {selected.size} {t('sp_integolf_import_selected_word', 'selected')}
            </span>
            <button
              onClick={commit}
              disabled={busy || selected.size === 0}
              style={{
                ...primaryBtn, marginTop: 0, marginLeft: 'auto',
                opacity: selected.size === 0 ? 0.5 : 1,
              }}
            >
              {busy && plan ? t('sp_integolf_import_importing', 'Importing…') : t('sp_integolf_import_commit_btn', '↑ Import selected')}
            </button>
          </div>
        </div>
      )}

      {result?.result && (
        <div style={{ marginTop: 16, padding: 12, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8 }}>
          <div style={{ fontWeight: 600, color: '#15803d', marginBottom: 6 }}>
            {t('sp_integolf_import_done', '✓ Import complete')}
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#334155', lineHeight: 1.6 }}>
            <li><b>{result.result.coursesCreated?.length || 0}</b> {t('sp_integolf_import_courses_created', 'course(s) created')}</li>
            <li><b>{result.result.cipUpserted?.length || 0}</b> {t('sp_integolf_import_creds_wired', 'credential row(s) wired')}</li>
            <li><b>{result.result.skipped?.length || 0}</b> {t('sp_integolf_import_entries_skipped', 'entr(ies) skipped (unselected or unrecognized)')}</li>
          </ul>
        </div>
      )}
    </Card>
  )
}

const smallToggleBtn = { padding: '4px 10px', background: '#fff', color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 12, cursor: 'pointer' }

const labelStyle = { display: 'flex', flexDirection: 'column', fontSize: 13, color: '#334155', marginTop: 16, fontWeight: 500 }
const selectStyle = { marginTop: 6, padding: '8px 10px', fontSize: 14, border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff' }
const primaryBtn = { marginTop: 20, padding: '10px 18px', fontSize: 14, fontWeight: 600, background: 'var(--brand-600, #00d19f)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }
const errorStyle = { color: '#b91c1c', fontSize: 13, marginTop: 12 }
const linkBtn = { background: 'none', border: 'none', color: '#2563eb', fontWeight: 600, fontSize: 12, cursor: 'pointer', padding: 0 }
const testInput = { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', color: '#0f172a' }
const th = { padding: '6px 8px' }
const thNum = { padding: '6px 8px', textAlign: 'right' }
const td = { padding: '6px 8px' }
const tdNum = { padding: '6px 8px', textAlign: 'right' }
